import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Calendar,
  BarChart2,
  List,
  History,
  TrendingUp,
  AlertTriangle,
  Package,
  Plus,
  Trash2,
  Edit2,
  RefreshCw,
  Search,
  ChevronDown,
  ChevronUp,
  Settings,
  Check,
  X,
  Play,
  ArrowRight,
  ClipboardList,
  CheckCircle,
  AlertCircle,
  User,
  Clock,
  Filter,
  Eye,
  MoreVertical,
  RotateCcw
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";
import { pb } from "../services/pocketbase";
import { useCatalogContext } from "../contexts/CatalogContext";
import { resolveCatalogSku } from "../utils/skuUtils";
import {
  planningService,
  isRing,
  parseRingSku,
  getRingSkuWithSize,
  ALL_RING_SIZES,
  PLANNING_CATEGORIES,
  resolvePlanningCategory
} from "../services/planningService";

// Helper to format months
const MONTH_NAMES = [
  "",
  "Janeiro", "Fevereiro", "Março", "Abril",
  "Maio", "Junho", "Julho", "Agosto",
  "Setembro", "Outubro", "Novembro", "Dezembro"
];

const PRIORITY_LABELS = {
  urgente: "URGENTE",
  critico: "CRÍTICO",
  alto_sazonal: "ALTO ↑ SAZONAL",
  alto: "ALTO",
  medio_sazonal: "MÉDIO ↑ SAZONAL",
  medio: "MÉDIO",
  ok: "OK",
  sem_historico: "SEM HISTÓRICO"
};

const PRIORITY_CLASSES = {
  urgente: "bg-red-500 text-white animate-pulse border-red-600",
  critico: "bg-red-100 text-red-700 border-red-300 font-bold",
  alto_sazonal: "bg-orange-100 text-orange-850 border-orange-300 font-bold",
  alto: "bg-orange-50 text-orange-700 border-orange-200",
  medio_sazonal: "bg-yellow-100 text-yellow-800 border-yellow-300 font-medium",
  medio: "bg-yellow-50 text-yellow-700 border-yellow-200",
  ok: "bg-slate-100 text-slate-500 border-slate-200",
  sem_historico: "bg-purple-100 text-purple-700 border-purple-200"
};

const USER_PRIORITY_CLASSES = {
  Planejado: "bg-slate-100 text-slate-700 border-slate-350",
  Prioritario: "bg-orange-105 text-orange-800 border-orange-300 font-bold",
  Urgente: "bg-red-500 text-white animate-pulse border-red-650 font-black",
};


const findItemInCatalog = (sku, catalogMap) => {
  return resolveCatalogSku(sku, catalogMap);
};

// Help classify jewelry type
const resolveJewelryType = (sku, name, category) => {
  const s = String(sku).toUpperCase();
  const n = String(name).toUpperCase();
  const c = String(category).toUpperCase();
  
  if (s.startsWith("AN") || s.startsWith("ALI") || n.includes("ANEL") || n.includes("ALIANÇA") || c.includes("ANEL") || c.includes("ALIANÇA") || c.includes("ANÉIS")) {
    return "Anel";
  }
  if (s.startsWith("BR") || n.includes("BRINCO") || c.includes("BRINCO")) {
    return "Brinco";
  }
  if (s.startsWith("PG") || s.startsWith("PIN") || s.startsWith("BER") || n.includes("PINGENTE") || n.includes("BERLOQUE") || c.includes("PINGENTE") || c.includes("BERLOQUE")) {
    return "Pingente";
  }
  if (s.startsWith("PL") || n.includes("PULSEIRA") || c.includes("PULSEIRA")) {
    return "Pulseira";
  }
  if (s.startsWith("CR") || n.includes("CORRENTE") || c.includes("CORRENTE") || n.includes("GARGANTILHA") || c.includes("GARGANTILHA")) {
    return "Corrente";
  }
  return "Outros";
};

// Help classify differentiators
const resolveDifferentiator = (sku, name) => {
  const s = String(sku).toUpperCase();
  const n = String(name).toUpperCase();
  if (s.includes("-D") || s.endsWith("D") || n.includes("DOURADO") || n.includes("FOLEADO OURE")) {
    return "Dourado";
  }
  if (s.includes("-R") || s.endsWith("R") || n.includes("RÓDIO") || n.includes("RODIO")) {
    return "Ródio";
  }
  if (s.includes("-P") || s.endsWith("P") || n.includes("PRATA")) {
    return "Prata";
  }
  return "Outro/Nenhum";
};

export default function PlanningTab({ user, inventory }) {
  const { catalog, loading: loadingCatalog } = useCatalogContext();
  
  const [activeSubTab, setActiveSubTab] = useState(() => {
    return localStorage.getItem("planning_active_subtab") || "dashboard";
  });
  
  const [loading, setLoading] = useState(true);
  const [loadingMsg, setLoadingMsg] = useState("");
  
  // Configurations
  const [configs, setConfigs] = useState({
    id: "",
    prazo_fabricacao_dias: 15,
    buffer_seguranca_percentual: 30,
    janela_historico_meses: 6,
    oficina_capacidade_semanal: 0,
  });

  // Capacity & Lotes
  const [workshopCapacity, setWorkshopCapacity] = useState({
    weekly: 67,
    peSolicitado: 0,
    activePe: 0,
  });
  const [isEditingCapacity, setIsEditingCapacity] = useState(false);
  const [tempCapacity, setTempCapacity] = useState("");

  // Dynamic weekly capacity calculations based on inventory finalized in last 7 days
  const calculated7dCapacity = useMemo(() => {
    if (!Array.isArray(inventory)) return 0;
    
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    
    const start = new Date();
    start.setDate(end.getDate() - 6); // Last 7 days including today
    start.setHours(0, 0, 0, 0);

    const produced = inventory.filter(item => {
      if (item.isPE) return false;
      const history = Array.isArray(item.history) ? item.history : [];
      const finishedEvt = history.find(h => h.action === "finalizado_pe");
      if (finishedEvt && finishedEvt.date) {
        const d = new Date(finishedEvt.date);
        return d >= start && d <= end;
      }
      return false;
    });
    
    return produced.length;
  }, [inventory]);

  const weeklyCapacity = useMemo(() => {
    const overrideVal = Number(configs.oficina_capacidade_semanal);
    if (overrideVal && overrideVal > 0) {
      return overrideVal;
    }
    if (calculated7dCapacity && calculated7dCapacity > 0) {
      return calculated7dCapacity * 6; // Dynamic capacity is 6x the weekly finalized output
    }
    return 67 * 6; // Default fallback capacity (67 * 6 = 402)
  }, [configs.oficina_capacidade_semanal, calculated7dCapacity]);

  // Dynamic map of SKUs currently in the factory production queue
  const factoryProductionSkus = useMemo(() => {
    if (!Array.isArray(inventory)) return {};
    
    const map = {};
    inventory.forEach(item => {
      if (item.isPE && !["in_stock", "sold", "adjusted_out", "pe_interceptado"].includes(item.status)) {
        const sku = String(item.sku).toUpperCase().trim();
        map[sku] = (map[sku] || 0) + 1;
      }
    });
    return map;
  }, [inventory]);

  // Sync weeklyCapacity to workshopCapacity state
  useEffect(() => {
    setWorkshopCapacity(prev => ({
      ...prev,
      weekly: weeklyCapacity
    }));
  }, [weeklyCapacity]);

  const [lotes, setLotes] = useState([]);
  const [estoqueLotes, setEstoqueLotes] = useState([]);
  const [selectedLoteId, setSelectedLoteId] = useState(null);
  const [selectedLoteItems, setSelectedLoteItems] = useState([]);
  const [manualAddSku, setManualAddSku] = useState("");
  const [manualAddQty, setManualAddQty] = useState(1);
  
  // SKU Analysis calculations
  const [planningItems, setPlanningItems] = useState([]);
  const [hasLoadedItems, setHasLoadedItems] = useState(false);
  const [selectedSkus, setSelectedSkus] = useState(new Set());
  
  // Sazonalidade
  const [seasonalityData, setSeasonalityData] = useState([]);
  const [pendingSeasonality, setPendingSeasonality] = useState({});
  const [auditLogs, setAuditLogs] = useState([]);

  // Filters for SKU Analysis
  const [targetMonth, setTargetMonth] = useState(() => {
    const nextMonth = new Date().getMonth() + 2; // Next month 1-indexed
    return nextMonth > 12 ? 1 : nextMonth;
  });
  const [filterCategory, setFilterCategory] = useState("TODOS");
  const [filterType, setFilterType] = useState("TODOS");
  const [filterDifferentiator, setFilterDifferentiator] = useState("TODOS");
  const [filterCourseSaint, setFilterCourseSaint] = useState("");
  const [filterAro, setFilterAro] = useState("TODOS");
  const [filterSku, setFilterSku] = useState("");
  const [filterName, setFilterName] = useState("");
  const [filterSituation, setFilterSituation] = useState("TODOS");
  const [filterMinDef, setFilterMinDef] = useState("TODOS");
  const [filterWithHistoryOnly, setFilterWithHistoryOnly] = useState(false);
  const [filterFactoryStatus, setFilterFactoryStatus] = useState("TODOS");
  const [allocatedSkusMap, setAllocatedSkusMap] = useState({});

  const updateAllocatedSkusMap = async (currentLotes) => {
    const activeLotes = currentLotes.filter(l => l.status === "rascunho");
    const activeLoteIds = activeLotes.map(l => l.id);
    const allocatedMap = {};
    if (activeLoteIds.length > 0) {
      const filterStr = activeLoteIds.map(id => `lote_id = "${id}"`).join(" || ");
      try {
        const allocatedItems = await pb.collection("planejamento_demanda_items").getFullList({
          filter: filterStr,
          fields: "sku,lote_id"
        });
        allocatedItems.forEach(item => {
          const batch = activeLotes.find(l => l.id === item.lote_id);
          const batchName = batch ? batch.nome : "Lote Ativo";
          const sku = item.sku.toUpperCase().trim();
          if (!allocatedMap[sku]) {
            allocatedMap[sku] = [];
          }
          if (!allocatedMap[sku].includes(batchName)) {
            allocatedMap[sku].push(batchName);
          }
        });
      } catch (e) {
        console.warn("Error loading allocated items map:", e);
      }
    }
    setAllocatedSkusMap(allocatedMap);
  };

  const getGroupAllocatedBatches = (groupItem) => {
    const batches = new Set();
    groupItem.items.forEach(sub => {
      const bList = allocatedSkusMap[sub.sku.toUpperCase().trim()];
      if (bList) {
        bList.forEach(b => batches.add(b));
      }
    });
    return Array.from(batches);
  };
  
  // Pagination states for SKU Analysis
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  
  // Reset page number when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterCategory, filterType, filterDifferentiator, filterCourseSaint, filterAro, filterSku, filterName, filterSituation, filterMinDef, filterWithHistoryOnly, filterFactoryStatus, targetMonth]);
  
  // Rings base SKU expansion set
  const [expandedRings, setExpandedRings] = useState(new Set());
  
  // Defining minimum modal / inline editing
  const [editingItemForMin, setEditingItemForMin] = useState(null);
  const [newMinVal, setNewMinVal] = useState("");

  // Create Batch modal
  const [showCreateLoteModal, setShowCreateLoteModal] = useState(false);
  const [newLoteForm, setNewLoteForm] = useState({
    nome: "",
    observacoes: "",
    data_inicio_prevista: new Date().toISOString().split("T")[0]
  });

  const [notification, setNotification] = useState(null);

  const showNotification = (msg, type = "success") => {
    setNotification({ message: msg, type });
    setTimeout(() => setNotification(null), 4000);
  };

  // Persist active subtab
  useEffect(() => {
    localStorage.setItem("planning_active_subtab", activeSubTab);
  }, [activeSubTab]);

  // Load capacity indicators and batches list
  const loadCapacityAndLotes = async () => {
    try {
      const [peSolicitadoRes, activePeRes, lotesList, estoqueLotesList] = await Promise.all([
        pb.collection("inventory_items").getList(1, 1, {
          filter: 'status = "pe_solicitado" && reason = ""'
        }),
        pb.collection("inventory_items").getList(1, 1, {
          filter: 'isPE = true && status != "in_stock" && status != "sold" && status != "adjusted_out" && status != "pe_interceptado" && (status != "pe_solicitado" || reason != "") && reason != "pe_pre_escapulario" && situacao_fluxo != "suspenso"'
        }),
        pb.collection("planejamento_lotes").getFullList({ sort: "-created" }),
        pb.collection("estoque_lotes").getFullList({ sort: "-created" }).catch(() => [])
      ]);

      const countPeSolicitado = peSolicitadoRes.totalItems;
      const countActivePe = activePeRes.totalItems;

      return { countPeSolicitado, countActivePe, lotes: lotesList, estoqueLotes: estoqueLotesList };
    } catch (err) {
      console.error("Error loading capacity/lotes:", err);
      return { countPeSolicitado: 0, countActivePe: 0, lotes: [], estoqueLotes: [] };
    }
  };

  const updateLotesStates = useCallback((data) => {
    setLotes(data.lotes || []);
    setEstoqueLotes(data.estoqueLotes || []);
    setWorkshopCapacity(prev => ({
      ...prev,
      peSolicitado: data.countPeSolicitado || 0,
      activePe: data.countActivePe || 0,
    }));
  }, []);

  // Load initial configurations and data
  const loadInitialData = async () => {
    if (loadingCatalog) return;
    setLoading(true);
    try {
      setLoadingMsg("1/5: Buscando configurações de fabricação...");
      const conf = await planningService.fetchConfig();
      setConfigs(conf);

      setLoadingMsg("2/5: Carregando fatores de sazonalidade e histórico de auditoria...");
      const seasonality = await planningService.fetchSeasonality(PLANNING_CATEGORIES, user?.id);
      setSeasonalityData(seasonality);

      const logs = [];
      seasonality.forEach(s => {
        if (Array.isArray(s.history)) {
          s.history.forEach(h => {
            logs.push({
              id: `${s.id}_${h.updated_at}`,
              categoria: s.categoria,
              mes: s.mes,
              ...h
            });
          });
        }
      });
      logs.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
      setAuditLogs(logs.slice(0, 30));

      setLoadingMsg("3/5: Calculando fila da oficina e capacidade em tempo real...");
      const data = await loadCapacityAndLotes();
      updateLotesStates(data);
      await updateAllocatedSkusMap(data.lotes);
      
      // Calculate 7d capacity baseline
      const end = new Date();
      end.setHours(23, 59, 59, 999);
      const start = new Date();
      start.setDate(end.getDate() - 6);
      start.setHours(0, 0, 0, 0);

      const produced = inventory.filter(item => {
        if (item.isPE) return false;
        const history = Array.isArray(item.history) ? item.history : [];
        const finishedEvt = history.find(h => h.action === "finalizado_pe");
        if (finishedEvt && finishedEvt.date) {
          const d = new Date(finishedEvt.date);
          return d >= start && d <= end;
        }
        return false;
      });
      const localCalc7d = produced.length;
      
      setWorkshopCapacity({
        weekly: conf.oficina_capacidade_semanal || (localCalc7d * 6) || (67 * 6),
        peSolicitado: data.countPeSolicitado,
        activePe: data.countActivePe,
      });

      setLoadingMsg("4/5: Processando simulações de estoque mínimo e SKU analysis...");
      const nextMonth = new Date().getMonth() + 2;
      const targetM = nextMonth > 12 ? 1 : nextMonth;
      
      const targetParams = {
        mesAlvo: Number(targetM),
        prazoFabricacao: Number(conf.prazo_fabricacao_dias),
        considerarSazonalidade: true,
        incluirAneisSemHistorico: true,
        catalog,
        inventory
      };
      const result = await planningService.loadPlanningData(targetParams);
      setPlanningItems(result.items);
      setHasLoadedItems(true);

      setLoadingMsg("5/5: Finalizando carregamento do painel...");
      await new Promise(r => setTimeout(r, 600));
    } catch (e) {
      console.error(e);
      showNotification("Erro ao carregar dados iniciais.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, [loadingCatalog, inventory]);

  // Run on-demand SKU analysis based on filters & month
  const handleApplyFilters = async () => {
    setLoading(true);
    setLoadingMsg("Rodando simulações de demanda e histórico de vendas...");
    try {
      const targetParams = {
        mesAlvo: Number(targetMonth),
        prazoFabricacao: Number(configs.prazo_fabricacao_dias),
        considerarSazonalidade: true,
        incluirAneisSemHistorico: true,
        catalog,
        inventory
      };

      const result = await planningService.loadPlanningData(targetParams);
      setPlanningItems(result.items);
      setHasLoadedItems(true);
      setSelectedSkus(new Set());
      await updateAllocatedSkusMap(lotes);
      showNotification("Simulação executada com sucesso!");
    } catch (e) {
      console.error(e);
      showNotification("Erro ao calcular dados de planejamento.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Compute ring sizes list from loaded items
  const stockSizesAvailable = useMemo(() => {
    const sizes = new Set();
    planningItems.forEach(item => {
      if (item.ringSize) {
        sizes.add(item.ringSize);
      }
    });
    return Array.from(sizes).sort((a, b) => Number(a) - Number(b));
  }, [planningItems]);

  // Filter in-memory items cumulatively
  const filteredPlanningItems = useMemo(() => {
    if (!hasLoadedItems) return [];
    
    let list = [...planningItems];

    if (filterCategory !== "TODOS") {
      list = list.filter(i => i.categoria === filterCategory);
    }

    if (filterType !== "TODOS") {
      list = list.filter(i => {
        const itemCatalog = findItemInCatalog(i.sku, catalog);
        const type = resolveJewelryType(i.sku, i.produto_nome, itemCatalog ? itemCatalog.category : i.categoria);
        return type === filterType;
      });
    }

    if (filterDifferentiator !== "TODOS") {
      list = list.filter(i => {
        const diff = resolveDifferentiator(i.sku, i.produto_nome);
        return diff === filterDifferentiator;
      });
    }

    if (filterCourseSaint.trim() !== "") {
      const term = filterCourseSaint.toLowerCase().trim();
      list = list.filter(i => {
        const skuMatch = String(i.sku).toLowerCase().includes(term);
        const nameMatch = String(i.produto_nome).toLowerCase().includes(term);
        const catMatch = String(i.categoria).toLowerCase().includes(term);
        return skuMatch || nameMatch || catMatch;
      });
    }

    if (filterAro !== "TODOS") {
      list = list.filter(i => i.ringSize === filterAro);
    }

    if (filterSku.trim() !== "") {
      const term = filterSku.toUpperCase().trim();
      list = list.filter(i => i.sku.toUpperCase().includes(term));
    }

    if (filterName.trim() !== "") {
      const term = filterName.toLowerCase().trim();
      list = list.filter(i => i.produto_nome.toLowerCase().includes(term));
    }

    if (filterSituation !== "TODOS") {
      list = list.filter(i => {
        const min = i.estoque_minimo_definido !== null ? i.estoque_minimo_definido : i.estoque_minimo_sugerido;
        if (filterSituation === "OK") return i.estoque_atual >= min;
        if (filterSituation === "ABAIXO") return i.estoque_atual < min && i.estoque_atual > 0;
        if (filterSituation === "SEM ESTOQUE") return i.estoque_atual === 0 && min > 0;
        return true;
      });
    }

    if (filterMinDef !== "TODOS") {
      list = list.filter(i => {
        if (filterMinDef === "DEFINIDO") return i.estoque_minimo_definido !== null;
        if (filterMinDef === "SEM_DEFINIR") return i.estoque_minimo_definido === null;
        return true;
      });
    }

    if (filterWithHistoryOnly) {
      list = list.filter(i => i.prioridade !== "sem_historico");
    }

    if (filterFactoryStatus !== "TODOS") {
      list = list.filter(i => {
        const skuKey = i.sku.toUpperCase().trim();
        const inFactoryCount = factoryProductionSkus[skuKey] || 0;
        if (filterFactoryStatus === "EM_PRODUCAO") return inFactoryCount > 0;
        if (filterFactoryStatus === "SEM_PRODUCAO") return inFactoryCount === 0;
        return true;
      });
    }

    return list;
  }, [planningItems, hasLoadedItems, filterCategory, filterType, filterDifferentiator, filterCourseSaint, filterAro, filterSku, filterName, filterSituation, filterMinDef, filterWithHistoryOnly, filterFactoryStatus, factoryProductionSkus, catalog]);

  // Rings visual grouping for analysis
  const groupedPlanningItems = useMemo(() => {
    const list = [];
    const ringsGroupMap = {};

    filteredPlanningItems.forEach(item => {
      if (isRing(item.sku, item.categoria, catalog)) {
        const parsed = parseRingSku(item.sku);
        const baseSku = parsed.baseSku;
        if (!ringsGroupMap[baseSku]) {
          ringsGroupMap[baseSku] = {
            isGroup: true,
            sku: baseSku,
            produto_nome: item.produto_nome.replace(/\s*\(Aro\s*\d+\)/i, ""),
            categoria: item.categoria,
            estoque_atual: 0,
            estoque_minimo_definido: 0,
            estoque_minimo_sugerido: 0,
            lacuna: 0,
            quantidade_sugerida: 0,
            vendas_6_meses: 0,
            items: [],
          };
        }
        ringsGroupMap[baseSku].estoque_atual += item.estoque_atual;
        if (item.estoque_minimo_definido !== null) {
          ringsGroupMap[baseSku].estoque_minimo_definido += item.estoque_minimo_definido;
        }
        ringsGroupMap[baseSku].estoque_minimo_sugerido += item.estoque_minimo_sugerido;
        ringsGroupMap[baseSku].lacuna += item.lacuna;
        ringsGroupMap[baseSku].quantidade_sugerida += item.quantidade_sugerida;
        ringsGroupMap[baseSku].vendas_6_meses += item.vendas_6_meses;
        ringsGroupMap[baseSku].items.push(item);
      } else {
        list.push(item);
      }
    });

    Object.values(ringsGroupMap).forEach(g => {
      g.items.sort((a, b) => Number(a.ringSize) - Number(b.ringSize));
      list.push(g);
    });

    list.sort((a, b) => a.sku.localeCompare(b.sku));
    return list;
  }, [filteredPlanningItems, catalog]);

  const totalPages = Math.ceil(groupedPlanningItems.length / itemsPerPage) || 1;

  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return groupedPlanningItems.slice(start, start + itemsPerPage);
  }, [groupedPlanningItems, currentPage, itemsPerPage]);

  // Multi-selection SKU toggles
  const handleToggleSkuSelection = (sku, isChecked) => {
    setSelectedSkus(prev => {
      const next = new Set(prev);
      if (isChecked) next.add(sku);
      else next.delete(sku);
      return next;
    });
  };

  const handleToggleGroupSelection = (groupItem, isChecked) => {
    setSelectedSkus(prev => {
      const next = new Set(prev);
      groupItem.items.forEach(sub => {
        if (isChecked) next.add(sub.sku);
        else next.delete(sub.sku);
      });
      return next;
    });
  };

  const isGroupSelected = (groupItem) => {
    if (groupItem.items.length === 0) return false;
    return groupItem.items.every(sub => selectedSkus.has(sub.sku));
  };

  const handleSelectAllVisible = (isChecked) => {
    setSelectedSkus(prev => {
      const next = new Set(prev);
      paginatedItems.forEach(item => {
        if (item.isGroup) {
          item.items.forEach(sub => {
            if (isChecked) next.add(sub.sku);
            else next.delete(sub.sku);
          });
        } else {
          if (isChecked) next.add(item.sku);
          else next.delete(item.sku);
        }
      });
      return next;
    });
  };

  const isAllSelected = useMemo(() => {
    if (paginatedItems.length === 0) return false;
    return paginatedItems.every(item => {
      if (item.isGroup) return item.items.every(sub => selectedSkus.has(sub.sku));
      return selectedSkus.has(item.sku);
    });
  }, [paginatedItems, selectedSkus]);

  // Save defined stock minimums
  const handleSaveDefinedMin = async () => {
    if (!editingItemForMin) return;
    const num = parseInt(newMinVal, 10);
    const valueToSave = isNaN(num) ? null : num;
    
    setLoading(true);
    setLoadingMsg("Salvando estoque mínimo...");
    try {
      await planningService.saveMinimumStock(
        editingItemForMin.sku,
        valueToSave,
        editingItemForMin.estoque_minimo_sugerido,
        editingItemForMin.produto_nome,
        editingItemForMin.categoria,
        user?.id
      );

      showNotification(`Mínimo para ${editingItemForMin.sku} salvo.`);
      
      // Update local state in memory
      setPlanningItems(prev => prev.map(item => {
        if (item.sku === editingItemForMin.sku) {
          const effectiveMin = valueToSave !== null ? valueToSave : item.estoque_minimo_sugerido;
          const lacuna = Math.max(0, effectiveMin - item.estoque_atual);
          let quantidadeSugerida = 0;
          if (lacuna > 0) {
            quantidadeSugerida = Math.max(lacuna, Math.ceil(item.media_mensal));
          }
          
          let prioridade = "ok";
          if (item.media_mensal === 0) {
            prioridade = "sem_historico";
          } else if (item.estoque_atual === 0 && item.previsao_proximos_30_dias > 0) {
            prioridade = "urgente";
          } else if (item.estoque_atual > 0 && item.estoque_atual < effectiveMin * 0.3) {
            prioridade = "critico";
          } else if (item.estoque_atual >= effectiveMin * 0.3 && item.estoque_atual < effectiveMin * 0.7) {
            prioridade = "alto";
          } else if (item.estoque_atual >= effectiveMin * 0.7 && item.estoque_atual < effectiveMin) {
            prioridade = "medio";
          } else if (item.estoque_atual >= effectiveMin) {
            prioridade = "ok";
          }

          return {
            ...item,
            estoque_minimo_definido: valueToSave,
            tem_minimo_definido: valueToSave !== null,
            lacuna,
            quantidade_sugerida: quantidadeSugerida,
            quantidade_aprovada: quantidadeSugerida,
            prioridade
          };
        }
        return item;
      }));

      setEditingItemForMin(null);
      setNewMinVal("");
    } catch (e) {
      console.error(e);
      showNotification("Erro ao salvar estoque mínimo.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Lotes database actions
  const handleCreateLote = async () => {
    if (!newLoteForm.nome.trim()) {
      showNotification("Por favor, digite o nome do lote.", "warning");
      return;
    }
    setLoading(true);
    setLoadingMsg("Criando lote...");
    try {
      const lote = await planningService.createLote({
        nome: newLoteForm.nome.trim(),
        prioridade_geral: "medio",
        data_inicio_prevista: new Date(newLoteForm.data_inicio_prevista).toISOString(),
        observacoes: newLoteForm.observacoes.trim()
      });

      showNotification(`Lote "${lote.nome}" criado com sucesso!`);
      setShowCreateLoteModal(false);
      setNewLoteForm({
        nome: "",
        observacoes: "",
        data_inicio_prevista: new Date().toISOString().split("T")[0]
      });

      // Reload
      const data = await loadCapacityAndLotes();
      updateLotesStates(data);
      
      // If we had selected SKUs, allocate them directly to the new lote!
      if (selectedSkus.size > 0) {
        await handleAllocateSelectedToLote(lote.id);
      }
    } catch (e) {
      console.error(e);
      showNotification("Erro ao criar lote", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleAllocateSelectedToLote = async (loteId) => {
    if (selectedSkus.size === 0) return;

    // Check for already allocated items in active batches and factory production
    const itemsToAllocate = planningItems.filter(i => selectedSkus.has(i.sku));
    const warnings = [];

    itemsToAllocate.forEach(i => {
      const skuKey = i.sku.toUpperCase().trim();
      const inLotes = allocatedSkusMap[skuKey];
      const inFactoryCount = factoryProductionSkus[skuKey];

      const parts = [];
      if (inLotes && inLotes.length > 0) {
        parts.push(`lote: ${inLotes.join(", ")}`);
      }
      if (inFactoryCount && inFactoryCount > 0) {
        parts.push(`produção na fábrica: ${inFactoryCount} pçs`);
      }

      if (parts.length > 0) {
        warnings.push(`• ${i.sku} (${parts.join(" / ")})`);
      }
    });

    if (warnings.length > 0) {
      const confirm = window.confirm(`Atenção: Os seguintes itens possuem fluxo de produção ativo:\n\n${warnings.join("\n")}\n\nDeseja adicionar mesmo assim?`);
      if (!confirm) return;
    }

    setLoading(true);
    setLoadingMsg("Alocando itens selecionados no lote...");
    try {
      const itemsToAllocateNow = planningItems.filter(i => selectedSkus.has(i.sku));

      for (const item of itemsToAllocateNow) {
        // Check if item already exists in this batch
        let existing = null;
        try {
          existing = await pb.collection("planejamento_demanda_items").getFirstListItem(`lote_id = "${loteId}" && sku = "${item.sku}"`);
        } catch (e) {}

        const payload = {
          lote_id: loteId,
          sku: item.sku,
          produto_nome: item.produto_nome,
          categoria: item.categoria,
          estoque_atual: Number(item.estoque_atual),
          estoque_minimo: Number(item.estoque_minimo_definido !== null ? item.estoque_minimo_definido : item.estoque_minimo_sugerido),
          lacuna: Number(item.lacuna),
          quantidade_sugerida: Number(item.quantidade_sugerida),
          quantidade_aprovada: Number(item.quantidade_sugerida || 0),
          prioridade: "Planejado", // Prioridade de produção padrão inicial
          fator_sazonalidade_aplicado: Number(item.fator_sazonalidade_aplicado || 1.0),
          pressao_sazonalidade: Number(item.previsao_proximos_30_dias ? (item.previsao_proximos_30_dias / (item.media_mensal || 1)) : 1.0),
          status_item: "alocado_lote",
          enviado_fabrica: false,
        };

        if (existing) {
          await pb.collection("planejamento_demanda_items").update(existing.id, {
            lote_id: loteId,
            quantidade_aprovada: existing.quantidade_aprovada + payload.quantidade_aprovada,
            status_item: "alocado_lote",
            prioridade: "Planejado", // Reset/definir como planejado ao alocar
          });
        } else {
          await pb.collection("planejamento_demanda_items").create(payload);
        }
      }

      // Recalculate lote totals
      const loteItems = await pb.collection("planejamento_demanda_items").getFullList({
        filter: `lote_id = "${loteId}"`
      });
      const totalItens = loteItems.length;
      const totalPecas = loteItems.reduce((acc, curr) => acc + (curr.quantidade_aprovada || 0), 0);

      const priorityOrder = { Urgente: 2, Prioritario: 1, Planejado: 0 };
      let highestPriority = "Planejado";
      let maxScore = -1;
      loteItems.forEach(li => {
        const score = priorityOrder[li.prioridade] || 0;
        if (score > maxScore) {
          maxScore = score;
          highestPriority = li.prioridade;
        }
      });

      const updatedLote = await pb.collection("planejamento_lotes").update(loteId, {
        total_itens: totalItens,
        total_pecas: totalPecas,
        prioridade_geral: highestPriority
      });

      showNotification(`Itens alocados no lote "${updatedLote.nome}" com sucesso!`);
      setSelectedSkus(new Set());
      
      // Refresh lote view if selected
      const data = await loadCapacityAndLotes();
      updateLotesStates(data);
      await updateAllocatedSkusMap(data.lotes);

      if (selectedLoteId === loteId) {
        handleSelectLote(loteId);
      }
    } catch (e) {
      console.error(e);
      showNotification("Erro ao alocar itens no lote", "error");
    } finally {
      setLoading(false);
    }
  };

  // Load items when selecting a lote
  const handleSelectLote = async (loteId) => {
    setSelectedLoteId(loteId);
    if (!loteId) {
      setSelectedLoteItems([]);
      return;
    }
    setLoading(true);
    setLoadingMsg("Carregando itens do lote...");
    try {
      const items = await pb.collection("planejamento_demanda_items").getFullList({
        filter: `lote_id = "${loteId}"`
      });
      setSelectedLoteItems(items);

      // Re-load capacity and lotes list to make sure we show correct totals
      const data = await loadCapacityAndLotes();
      updateLotesStates(data);
    } catch (e) {
      console.error(e);
      showNotification("Erro ao carregar itens do lote", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateLoteStartDate = async (loteId, dateStr) => {
    if (!dateStr) return;
    try {
      const lote = lotes.find(l => l.id === loteId);
      if (!lote) return;
      const dataInicio = new Date(dateStr);
      const dataEntrega = new Date(dataInicio);
      dataEntrega.setDate(dataEntrega.getDate() + (configs.prazo_fabricacao_dias || 15));
      
      const updated = await pb.collection("planejamento_lotes").update(loteId, {
        data_inicio_prevista: dataInicio.toISOString(),
        data_entrega_prevista: dataEntrega.toISOString(),
      });
      
      setLotes(prev => prev.map(l => l.id === loteId ? updated : l));
      showNotification("Data de início e data de entrega recalculadas.");
    } catch (e) {
      console.error(e);
      showNotification("Erro ao atualizar data do lote", "error");
    }
  };

  const handleUpdateLoteItemQty = async (itemId, val) => {
    const qty = parseInt(val, 10);
    if (isNaN(qty) || qty < 0) return;
    try {
      await pb.collection("planejamento_demanda_items").update(itemId, {
        quantidade_aprovada: qty,
        status_item: qty === 0 ? "rejeitado" : "alocado_lote",
      });

      setSelectedLoteItems(prev => prev.map(i => i.id === itemId ? { ...i, quantidade_aprovada: qty, status_item: qty === 0 ? "rejeitado" : "alocado_lote" } : i));
      
      // Update lote totals
      const updatedItems = selectedLoteItems.map(i => i.id === itemId ? { ...i, quantidade_aprovada: qty } : i);
      const totalItens = updatedItems.length;
      const totalPecas = updatedItems.reduce((acc, curr) => acc + (curr.quantidade_aprovada || 0), 0);

      const priorityOrder = { Urgente: 2, Prioritario: 1, Planejado: 0 };
      let highestPriority = "Planejado";
      let maxScore = -1;
      updatedItems.forEach(item => {
        const score = priorityOrder[item.prioridade] || 0;
        if (score > maxScore) {
          maxScore = score;
          highestPriority = item.prioridade;
        }
      });

      const updatedLote = await pb.collection("planejamento_lotes").update(selectedLoteId, {
        total_itens: totalItens,
        total_pecas: totalPecas,
        prioridade_geral: highestPriority,
      });

      setLotes(prev => prev.map(l => l.id === selectedLoteId ? updatedLote : l));
    } catch (e) {
      console.error(e);
    }
  };

  const handleUpdateLoteItemPriority = async (itemId, newPriority) => {
    try {
      await pb.collection("planejamento_demanda_items").update(itemId, {
        prioridade: newPriority
      });

      setSelectedLoteItems(prev => prev.map(i => i.id === itemId ? { ...i, prioridade: newPriority } : i));
      
      // Update lote totals and overall priority
      const updatedItems = selectedLoteItems.map(i => i.id === itemId ? { ...i, prioridade: newPriority } : i);
      const totalItens = updatedItems.length;
      const totalPecas = updatedItems.reduce((acc, curr) => acc + (curr.quantidade_aprovada || 0), 0);

      const priorityOrder = { Urgente: 2, Prioritario: 1, Planejado: 0 };
      let highestPriority = "Planejado";
      let maxScore = -1;
      updatedItems.forEach(item => {
        const score = priorityOrder[item.prioridade] || 0;
        if (score > maxScore) {
          maxScore = score;
          highestPriority = item.prioridade;
        }
      });

      const updatedLote = await pb.collection("planejamento_lotes").update(selectedLoteId, {
        total_itens: totalItens,
        total_pecas: totalPecas,
        prioridade_geral: highestPriority,
      });

      setLotes(prev => prev.map(l => l.id === selectedLoteId ? updatedLote : l));
      showNotification("Prioridade do item atualizada.");
    } catch (e) {
      console.error(e);
      showNotification("Erro ao atualizar prioridade", "error");
    }
  };

  const handleRemoveLoteItem = async (itemId) => {
    if (!window.confirm("Remover este item do lote?")) return;
    try {
      await pb.collection("planejamento_demanda_items").update(itemId, {
        lote_id: "",
        status_item: "pendente",
      });

      const updatedItems = selectedLoteItems.filter(i => i.id !== itemId);
      setSelectedLoteItems(updatedItems);

      // Recalculate lote totals
      const totalItens = updatedItems.length;
      const totalPecas = updatedItems.reduce((acc, curr) => acc + (curr.quantidade_aprovada || 0), 0);

      const priorityOrder = { Urgente: 2, Prioritario: 1, Planejado: 0 };
      let highestPriority = "Planejado";
      let maxScore = -1;
      updatedItems.forEach(item => {
        const score = priorityOrder[item.prioridade] || 0;
        if (score > maxScore) {
          maxScore = score;
          highestPriority = item.prioridade;
        }
      });

      const updatedLote = await pb.collection("planejamento_lotes").update(selectedLoteId, {
        total_itens: totalItens,
        total_pecas: totalPecas,
        prioridade_geral: highestPriority,
      });

      setLotes(prev => prev.map(l => l.id === selectedLoteId ? updatedLote : l));
      showNotification("Item removido do lote.");
    } catch (e) {
      console.error(e);
      showNotification("Erro ao remover item do lote", "error");
    }
  };

  const handleSendLoteToFactory = async (loteId) => {
    if (!window.confirm("Confirmar envio deste lote para a fábrica? Isso enviará o lote para o buffer de aprovação de governança do Estoque.")) return;
    setLoading(true);
    setLoadingMsg("Enviando lote para o buffer...");
    try {
      await planningService.sendLoteToFactory(loteId, user);
      showNotification("Lote enviado para o buffer de aprovação com sucesso!");

      const data = await loadCapacityAndLotes();
      updateLotesStates(data);

      // Reload selected lote items
      handleSelectLote(loteId);
    } catch (e) {
      console.error(e);
      showNotification(`Erro ao enviar lote: ${e.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelLote = async (loteId) => {
    if (!window.confirm("ATENÇÃO: Excluir permanentemente este lote de planejamento e retornar seus itens para pendentes?")) return;
    setLoading(true);
    setLoadingMsg("Excluindo lote...");
    try {
      const items = await pb.collection("planejamento_demanda_items").getFullList({
        filter: `lote_id = "${loteId}"`
      });

      await Promise.all(items.map(i => 
        pb.collection("planejamento_demanda_items").update(i.id, {
          lote_id: "",
          status_item: "pendente",
        })
      ));

      await pb.collection("planejamento_lotes").delete(loteId);
      
      showNotification("Lote excluído com sucesso.");
      setSelectedLoteId(null);
      setSelectedLoteItems([]);

      const data = await loadCapacityAndLotes();
      updateLotesStates(data);
    } catch (e) {
      console.error(e);
      showNotification("Erro ao excluir lote", "error");
    } finally {
      setLoading(false);
    }
  };

  // Seasonality updates
  const handleSeasonalityLocalChange = (recordId, val) => {
    setPendingSeasonality(prev => ({
      ...prev,
      [recordId]: val
    }));
  };

  const handleSaveSeasonalityChanges = async () => {
    const entries = Object.entries(pendingSeasonality);
    if (entries.length === 0) return;

    for (const [id, val] of entries) {
      const num = parseFloat(val);
      if (isNaN(num) || num < 0.1 || num > 2.0) {
        showNotification("Os fatores de sazonalidade devem estar entre 0.1 e 2.0", "error");
        return;
      }
    }

    setLoading(true);
    setLoadingMsg("Salvando sazonalidade...");
    try {
      await Promise.all(entries.map(async ([id, val]) => {
        const num = parseFloat(val);
        const original = seasonalityData.find(s => s.id === id);
        const oldVal = original ? original.fator : 1.0;
        if (num !== oldVal) {
          await planningService.updateSeasonalityCell(id, num, oldVal, user?.name, user?.id);
        }
      }));

      showNotification("Fatores de sazonalidade atualizados com sucesso!");
      setPendingSeasonality({});
      await loadInitialData();
    } catch (e) {
      console.error(e);
      showNotification("Erro ao salvar fatores de sazonalidade.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleResetRow = async (category) => {
    if (!window.confirm(`Resetar todos os meses da categoria "${category}" para 1.0?`)) return;
    try {
      await planningService.resetSeasonalityRow(category, user?.name, user?.id);
      showNotification("Fatores da categoria resetados!");
      setPendingSeasonality({});
      await loadInitialData();
    } catch (e) {
      showNotification("Erro ao resetar linha.", "error");
    }
  };

  // Config tab updates
  const handleSaveConfigs = async () => {
    setLoading(true);
    setLoadingMsg("Salvando configurações globais...");
    try {
      await planningService.updateConfig(configs.id, {
        prazo_fabricacao_dias: Number(configs.prazo_fabricacao_dias),
        buffer_seguranca_percentual: Number(configs.buffer_seguranca_percentual),
        janela_historico_meses: Number(configs.janela_historico_meses),
        oficina_capacidade_semanal: Number(configs.oficina_capacidade_semanal || 67),
      }, user?.id);

      showNotification("Configurações atualizadas com sucesso!");
    } catch (e) {
      console.error(e);
      showNotification("Erro ao salvar configurações", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCapacityOverride = async () => {
    const val = tempCapacity === "" ? 0 : Number(tempCapacity);
    if (isNaN(val) || val < 0) {
      showNotification("Por favor, digite um número válido para a capacidade.", "error");
      return;
    }
    setLoading(true);
    setLoadingMsg("Salvando nova capacidade...");
    try {
      await planningService.updateConfig(configs.id, {
        prazo_fabricacao_dias: Number(configs.prazo_fabricacao_dias),
        buffer_seguranca_percentual: Number(configs.buffer_seguranca_percentual),
        janela_historico_meses: Number(configs.janela_historico_meses),
        oficina_capacidade_semanal: val,
      }, user?.id);

      setConfigs(prev => ({ ...prev, oficina_capacidade_semanal: val }));
      setIsEditingCapacity(false);
      showNotification(val === 0 ? "Override removido! Usando capacidade calculada." : "Capacidade da oficina atualizada com sucesso!");
    } catch (e) {
      console.error(e);
      showNotification("Erro ao atualizar capacidade da oficina", "error");
    } finally {
      setLoading(false);
    }
  };

  // Dashboard Stats Calculations
  const dashboardStats = useMemo(() => {
    let belowMin = 0;
    let outOfStock = 0;

    planningItems.forEach(item => {
      const min = item.estoque_minimo_definido !== null ? item.estoque_minimo_definido : item.estoque_minimo_sugerido;
      if (item.estoque_atual < min) {
        belowMin++;
        if (item.estoque_atual === 0 && min > 0) {
          outOfStock++;
        }
      }
    });

    // Last batch/lote info
    const lastLote = lotes[0];
    const lastDate = lastLote ? new Date(lastLote.created).toLocaleDateString("pt-BR") : "Nenhum";
    const lastStatus = lastLote ? lastLote.status.toUpperCase() : "-";

    const nextMonthIndex = new Date().getMonth() + 2;
    const nextMonthName = MONTH_NAMES[nextMonthIndex > 12 ? 1 : nextMonthIndex];

    return {
      belowMin,
      outOfStock,
      lastPlan: `${lastDate} [${lastStatus}]`,
      nextMonthName
    };
  }, [planningItems, lotes]);

  // Governança Health Metrics Calculations
  const governanceStats = useMemo(() => {
    // 1. Lead Time Médio
    let leadTimeTotal = 0;
    let leadTimeCount = 0;
    estoqueLotes.forEach(l => {
      if (l.status === "concluido" && l.created && l.updated) {
        const start = new Date(l.created);
        const end = new Date(l.updated);
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) || 1;
        leadTimeTotal += diffDays;
        leadTimeCount++;
      }
    });
    const avgLeadTime = leadTimeCount > 0 ? (leadTimeTotal / leadTimeCount).toFixed(1) : "N/A";

    // 2. Taxa de Origem
    let countPlanejamento = 0;
    let countEscritorio = 0;
    let countOficina = 0;
    estoqueLotes.forEach(l => {
      if (l.origem === "planejamento") countPlanejamento++;
      else if (l.origem === "escritorio") countEscritorio++;
      else if (l.origem === "solicitacao_oficina" || l.origem === "oficina") countOficina++;
    });
    const totalLotesOrigem = countPlanejamento + countEscritorio + countOficina || 1;
    const pctPlanejamento = Math.round((countPlanejamento / totalLotesOrigem) * 100);
    const pctEscritorio = Math.round((countEscritorio / totalLotesOrigem) * 100);
    const pctOficina = Math.round((countOficina / totalLotesOrigem) * 105); // adjusted weight
    const sum = pctPlanejamento + pctEscritorio + pctOficina || 1;
    const normPlanejamento = Math.round((pctPlanejamento / sum) * 100);
    const normEscritorio = Math.round((pctEscritorio / sum) * 100);
    const normOficina = 100 - normPlanejamento - normEscritorio;

    // 3. Taxa de Regressão de Status
    const STATUS_ORDER = [
      "pe_solicitado",
      "pe_imprimindo",
      "pe_fundicao",
      "pe_revisao",
      "pe_conferencia",
      "in_stock"
    ];
    let totalTransitions = 0;
    let totalRegressions = 0;

    if (Array.isArray(inventory)) {
      inventory.forEach(item => {
        const history = Array.isArray(item.history) ? item.history : [];
        const transitions = history.filter(h => h.action === "status_changed" || (h.type === "system" && h.text && h.text.includes("->")));
        
        for (let i = 0; i < transitions.length; i++) {
          const t = transitions[i];
          if (t.text && t.text.includes("->")) {
            const parts = t.text.split("->");
            const fromLabel = parts[0].trim();
            const toLabel = parts[1].trim();
            
            const getStatusIdFromLabel = (lbl) => {
              const l = lbl.toLowerCase();
              if (l.includes("solicit")) return "pe_solicitado";
              if (l.includes("imprim")) return "pe_imprimindo";
              if (l.includes("fundi")) return "pe_fundicao";
              if (l.includes("revis")) return "pe_revisao";
              if (l.includes("confer")) return "pe_conferencia";
              if (l.includes("estoqu")) return "in_stock";
              return null;
            };
            
            const fromId = getStatusIdFromLabel(fromLabel);
            const toId = getStatusIdFromLabel(toLabel);
            
            if (fromId && toId) {
              totalTransitions++;
              const fromIdx = STATUS_ORDER.indexOf(fromId);
              const toIdx = STATUS_ORDER.indexOf(toId);
              if (fromIdx !== -1 && toIdx !== -1 && toIdx < fromIdx) {
                totalRegressions++;
              }
            }
          }
        }
      });
    }

    const regressionRate = totalTransitions > 0 ? ((totalRegressions / totalTransitions) * 100).toFixed(1) : "0.0";

    return {
      avgLeadTime,
      pctPlanejamento: normPlanejamento,
      pctEscritorio: normEscritorio,
      pctOficina: normOficina,
      regressionRate
    };
  }, [estoqueLotes, inventory]);

  // Recharts Chart Data
  const chartData = useMemo(() => {
    const map = {};
    planningItems.forEach(item => {
      const min = item.estoque_minimo_definido !== null ? item.estoque_minimo_definido : item.estoque_minimo_sugerido;
      if (item.estoque_atual < min) {
        map[item.categoria] = (map[item.categoria] || 0) + 1;
      }
    });
    return Object.entries(map).map(([name, count]) => ({
      name,
      "SKUs Defasados": count
    })).sort((a, b) => b["SKUs Defasados"] - a["SKUs Defasados"]);
  }, [planningItems]);

  const sentBatchesChartData = useMemo(() => {
    const sentLotes = lotes.filter(l => l.status !== "rascunho");
    
    const sortedLotes = [...sentLotes].sort((a, b) => {
      const dA = new Date(a.enviado_em || a.updated || a.created);
      const dB = new Date(b.enviado_em || b.updated || b.created);
      return dA - dB;
    });

    const labelMap = {};
    sortedLotes.forEach(l => {
      const dateStr = l.enviado_em || l.updated || l.created;
      if (!dateStr) return;
      const d = new Date(dateStr);
      const label = d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
      labelMap[label] = (labelMap[label] || 0) + (l.total_pecas || 0);
    });

    return Object.entries(labelMap).map(([date, count]) => ({
      date,
      "Peças Enviadas": count
    }));
  }, [lotes]);

  // Top 10 Critical items
  const topCriticalItems = useMemo(() => {
    return [...planningItems]
      .filter(i => {
        const min = i.estoque_minimo_definido !== null ? i.estoque_minimo_definido : i.estoque_minimo_sugerido;
        return min > 0 && i.estoque_atual < min;
      })
      .map(i => {
        const min = i.estoque_minimo_definido !== null ? i.estoque_minimo_definido : i.estoque_minimo_sugerido;
        const pct = min > 0 ? (i.estoque_atual / min) * 100 : 100;
        return {
          ...i,
          percent: Math.round(pct),
          minVal: min
        };
      })
      .sort((a, b) => a.percent - b.percent)
      .slice(0, 10);
  }, [planningItems]);

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] w-full overflow-hidden bg-slate-50 font-sans">
      
      {/* 5-SUBTAB HEADER NAVIGATION */}
      <div className="flex px-6 pt-4 border-b border-slate-200 bg-white gap-2 shrink-0 overflow-x-auto [&::-webkit-scrollbar]:hidden">
        <button
          onClick={() => setActiveSubTab("dashboard")}
          className={`flex items-center gap-2 px-4 py-2 border-b-2 font-bold text-xs transition-all uppercase tracking-wider whitespace-nowrap ${
            activeSubTab === "dashboard"
              ? "border-blue-600 text-blue-600"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <BarChart2 size={15} /> Dashboard
        </button>

        <button
          onClick={() => setActiveSubTab("analise")}
          className={`flex items-center gap-2 px-4 py-2 border-b-2 font-bold text-xs transition-all uppercase tracking-wider whitespace-nowrap ${
            activeSubTab === "analise"
              ? "border-blue-600 text-blue-600"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <List size={15} /> Análise de SKUs
        </button>

        <button
          onClick={() => setActiveSubTab("lotes")}
          className={`flex items-center gap-2 px-4 py-2 border-b-2 font-bold text-xs transition-all uppercase tracking-wider whitespace-nowrap ${
            activeSubTab === "lotes"
              ? "border-blue-600 text-blue-600"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <ClipboardList size={15} /> Meus Lotes
        </button>

        <button
          onClick={() => setActiveSubTab("sazonalidade")}
          className={`flex items-center gap-2 px-4 py-2 border-b-2 font-bold text-xs transition-all uppercase tracking-wider whitespace-nowrap ${
            activeSubTab === "sazonalidade"
              ? "border-blue-600 text-blue-600"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <Calendar size={15} /> Sazonalidade
        </button>

        <button
          onClick={() => setActiveSubTab("configuracoes")}
          className={`flex items-center gap-2 px-4 py-2 border-b-2 font-bold text-xs transition-all uppercase tracking-wider whitespace-nowrap ${
            activeSubTab === "configuracoes"
              ? "border-blue-600 text-blue-600"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <Settings size={15} /> Configurações
        </button>
      </div>

      {notification && (
        <div
          className={`fixed top-4 right-4 z-[100] p-4 rounded-lg shadow-xl text-white flex items-center gap-3 animate-fade-in ${
            notification.type === "error" ? "bg-red-500" : "bg-emerald-600"
          }`}
        >
          {notification.type === "error" ? <AlertCircle size={20} /> : <CheckCircle size={20} />}
          <span className="font-semibold text-xs">{notification.message}</span>
        </div>
      )}

      {/* SUBTABS VIEWPORT */}
      <div className="flex-1 overflow-y-auto p-6 min-h-0 relative">
        {loading && (
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl p-6 shadow-2xl border border-slate-100 max-w-sm w-full space-y-4 text-center animate-scale-in">
              <div className="relative w-14 h-14 mx-auto">
                <div className="absolute inset-0 border-4 border-blue-100 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-blue-600 rounded-full border-t-transparent animate-spin"></div>
                <div className="absolute inset-0 flex items-center justify-center text-blue-600">
                  <TrendingUp size={20} className="animate-pulse" />
                </div>
              </div>
              <div className="space-y-1">
                <h4 className="font-black text-xs text-slate-800 uppercase tracking-wider">Carregando Planejamento</h4>
                <p className="text-[11px] text-slate-500 font-medium">{loadingMsg}</p>
              </div>
              
              <div className="border-t pt-3.5 text-left text-[10px] text-slate-450 space-y-2 font-bold uppercase tracking-wider">
                <div className="flex items-center gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full ${loadingMsg.includes("1/5") ? "bg-blue-600 animate-ping" : loadingMsg.includes("2/5") || loadingMsg.includes("3/5") || loadingMsg.includes("4/5") || loadingMsg.includes("5/5") ? "bg-emerald-500" : "bg-slate-200"}`}></span>
                  <span className={loadingMsg.includes("1/5") ? "text-slate-800 font-black" : (loadingMsg.includes("2/5") || loadingMsg.includes("3/5") || loadingMsg.includes("4/5") || loadingMsg.includes("5/5") ? "text-emerald-600" : "")}>1. Configurações</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full ${loadingMsg.includes("2/5") ? "bg-blue-600 animate-ping" : loadingMsg.includes("3/5") || loadingMsg.includes("4/5") || loadingMsg.includes("5/5") ? "bg-emerald-500" : "bg-slate-200"}`}></span>
                  <span className={loadingMsg.includes("2/5") ? "text-slate-800 font-black" : (loadingMsg.includes("3/5") || loadingMsg.includes("4/5") || loadingMsg.includes("5/5") ? "text-emerald-600" : "")}>2. Sazonalidade</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full ${loadingMsg.includes("3/5") ? "bg-blue-600 animate-ping" : loadingMsg.includes("4/5") || loadingMsg.includes("5/5") ? "bg-emerald-500" : "bg-slate-200"}`}></span>
                  <span className={loadingMsg.includes("3/5") ? "text-slate-800 font-black" : (loadingMsg.includes("4/5") || loadingMsg.includes("5/5") ? "text-emerald-600" : "")}>3. Fila da Oficina</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full ${loadingMsg.includes("4/5") ? "bg-blue-600 animate-ping" : loadingMsg.includes("5/5") ? "bg-emerald-500" : "bg-slate-200"}`}></span>
                  <span className={loadingMsg.includes("4/5") ? "text-slate-800 font-black" : (loadingMsg.includes("5/5") ? "text-emerald-600" : "")}>4. Simulações SKU</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full ${loadingMsg.includes("5/5") ? "bg-emerald-500 animate-pulse" : "bg-slate-200"}`}></span>
                  <span className={loadingMsg.includes("5/5") ? "text-emerald-600 font-black" : ""}>5. Finalização</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========================================== */}
        {/* SUBTAB 1: DASHBOARD */}
        {/* ========================================== */}
        {activeSubTab === "dashboard" && (
          <div className="space-y-6 animate-fade-in">
            
            {/* Top row cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              
              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Abaixo do Mínimo</span>
                  <p className={`text-2xl font-black mt-1 ${dashboardStats.belowMin > 0 ? "text-red-650" : "text-slate-800"}`}>
                    {dashboardStats.belowMin}
                  </p>
                </div>
                <div className={`p-3 rounded-lg ${dashboardStats.belowMin > 0 ? "bg-red-50 text-red-500" : "bg-slate-50 text-slate-500"}`}>
                  <AlertTriangle size={24} />
                </div>
              </div>

              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Estoque Zerado</span>
                  <p className={`text-2xl font-black mt-1 ${dashboardStats.outOfStock > 0 ? "text-red-650 animate-pulse" : "text-slate-800"}`}>
                    {dashboardStats.outOfStock}
                  </p>
                </div>
                <div className={`p-3 rounded-lg ${dashboardStats.outOfStock > 0 ? "bg-red-105 text-red-700" : "bg-slate-50 text-slate-500"}`}>
                  <Package size={24} />
                </div>
              </div>

              {/* Real-time Workshop Capacity Card */}
              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between md:col-span-2">
                <div className="flex-1">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Capacidade Total da Oficina (6 Etapas)</span>
                  <div className="flex items-center gap-4 mt-2">
                    <div>
                      <p className="text-[10px] text-slate-450">Disponível</p>
                      <p className={`text-xl font-black ${workshopCapacity.weekly - workshopCapacity.peSolicitado <= 10 ? "text-red-600" : "text-emerald-600"}`}>
                        {Math.max(0, workshopCapacity.weekly - workshopCapacity.peSolicitado)} <span className="text-xs text-slate-400">/ {workshopCapacity.weekly}</span>
                      </p>
                    </div>
                    <div className="w-px h-8 bg-slate-200"></div>
                    <div>
                      <p className="text-[10px] text-slate-450">Fila Solicitações</p>
                      <p className="text-xl font-bold text-slate-700">{workshopCapacity.peSolicitado} pçs</p>
                    </div>
                    <div className="w-px h-8 bg-slate-200"></div>
                    <div>
                      <p className="text-[10px] text-slate-450">Em Produção</p>
                      <p className="text-xl font-bold text-blue-600">{workshopCapacity.activePe} pçs</p>
                    </div>
                  </div>
                  
                  {/* Progress bar */}
                  <div className="w-full bg-slate-100 h-2 rounded-full mt-3 overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all duration-500 ${
                        (workshopCapacity.peSolicitado / workshopCapacity.weekly) > 0.9 ? "bg-red-500" :
                        (workshopCapacity.peSolicitado / workshopCapacity.weekly) > 0.7 ? "bg-orange-500" : "bg-emerald-500"
                      }`}
                      style={{ width: `${Math.min(100, (workshopCapacity.peSolicitado / workshopCapacity.weekly) * 100)}%` }}
                    ></div>
                  </div>
                </div>
              </div>

              </div>

            {/* Governance Health Metrics Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              
              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Lead Time Médio (Produção)</span>
                  <p className="text-xl font-black mt-1 text-slate-800">
                    {governanceStats.avgLeadTime === "N/A" ? "N/A" : `${governanceStats.avgLeadTime} dias`}
                  </p>
                  <p className="text-[10px] text-slate-400 mt-1">Tempo médio de conclusão de lotes</p>
                </div>
                <div className="p-3 bg-purple-50 text-purple-600 rounded-lg">
                  <Clock size={22} />
                </div>
              </div>

              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Taxa de Regressão de Status</span>
                  <p className="text-xl font-black mt-1 text-red-650">
                    {governanceStats.regressionRate}%
                  </p>
                  <p className="text-[10px] text-slate-400 mt-1">Porcentagem de retrabalho / regressões</p>
                </div>
                <div className="p-3 bg-red-50 text-red-600 rounded-lg">
                  <RotateCcw size={22} />
                </div>
              </div>

              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
                <div className="flex-1">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Distribuição de Origem dos Lotes</span>
                  <div className="flex items-center gap-4 mt-2">
                    <div>
                      <p className="text-[9px] text-slate-400 font-bold uppercase">Plan.</p>
                      <p className="text-sm font-black text-blue-600">{governanceStats.pctPlanejamento}%</p>
                    </div>
                    <div className="w-px h-6 bg-slate-200"></div>
                    <div>
                      <p className="text-[9px] text-slate-400 font-bold uppercase">Escrit.</p>
                      <p className="text-sm font-black text-slate-700">{governanceStats.pctEscritorio}%</p>
                    </div>
                    <div className="w-px h-6 bg-slate-200"></div>
                    <div>
                      <p className="text-[9px] text-slate-400 font-bold uppercase">Ofic.</p>
                      <p className="text-sm font-black text-amber-650">{governanceStats.pctOficina}%</p>
                    </div>
                  </div>
                  <div className="w-full bg-slate-100 h-1.5 rounded-full mt-2.5 overflow-hidden flex">
                    <div className="bg-blue-500 h-full" style={{ width: `${governanceStats.pctPlanejamento}%` }}></div>
                    <div className="bg-slate-400 h-full" style={{ width: `${governanceStats.pctEscritorio}%` }}></div>
                    <div className="bg-amber-500 h-full" style={{ width: `${governanceStats.pctOficina}%` }}></div>
                  </div>
                </div>
              </div>

            </div>

            {/* Middle row: Chart + Critical Items + Active batches */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              
              {/* Graphic Category Deficits */}
              <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm lg:col-span-2 flex flex-col justify-between">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Defasagem por Categoria</h3>
                  <button 
                    onClick={() => {
                      setActiveSubTab("analise");
                    }}
                    className="text-[10px] font-black text-blue-600 uppercase hover:underline flex items-center gap-1"
                  >
                    Iniciar Nova Análise <ArrowRight size={12} />
                  </button>
                </div>
                
                <div className="flex-1 min-h-[260px]">
                  {chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                        <XAxis dataKey="name" stroke="#94A3B8" fontSize={10} tickLine={false} />
                        <YAxis stroke="#94A3B8" fontSize={10} tickLine={false} />
                        <Tooltip contentStyle={{ background: "#1E293B", border: "none", borderRadius: "8px", color: "white", fontSize: "11px" }} />
                        <Bar
                          dataKey="SKUs Defasados"
                          fill="#3B82F6"
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center text-xs text-slate-400 py-12">
                      <CheckCircle size={36} className="text-emerald-500 mb-2" />
                      Nenhum item abaixo do mínimo. Carregue uma simulação na Análise de SKUs para ver o panorama completo.
                    </div>
                  )}
                </div>
              </div>

              {/* Top 10 Critical SKUs */}
              <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
                <h3 className="text-xs font-bold text-slate-700 mb-4 uppercase tracking-wider border-b pb-2">SKUs mais Críticos</h3>
                <div className="flex-1 overflow-y-auto max-h-[250px] space-y-2.5 pr-1">
                  {topCriticalItems.length > 0 ? (
                    topCriticalItems.map(item => (
                      <div key={item.sku} className="flex justify-between items-center text-xs border-b border-slate-100 pb-2 last:border-0">
                        <div>
                          <strong className="font-mono text-slate-800 block text-[11px]">{item.sku}</strong>
                          <span className="text-[10px] text-slate-450 truncate block max-w-[150px]">{item.produto_nome}</span>
                        </div>
                        <div className="text-right">
                          <span className="font-black text-red-500 block">+{item.lacuna} pçs</span>
                          <span className="text-[10px] text-slate-400">{item.estoque_atual} / {item.minVal} estoque</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="h-full flex items-center justify-center text-xs text-slate-400 py-12">
                      Sem pendências críticas.
                    </div>
                  )}
                </div>
              </div>

            </div>

            {/* Active batches summary list */}
            <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
              <h3 className="text-xs font-bold text-slate-700 mb-4 uppercase tracking-wider">Lotes em Andamento (Montagem / Rascunhos)</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {lotes.filter(l => l.status === "rascunho").slice(0, 6).map(l => (
                  <div key={l.id} className="p-4 border rounded-xl bg-slate-50 hover:border-purple-300 transition-all flex flex-col justify-between space-y-3 relative shadow-inner">
                    <div className="flex justify-between items-start">
                      <strong className="text-xs text-slate-800 block font-bold truncate max-w-[170px]">{l.nome}</strong>
                      <span className="px-1.5 py-0.5 rounded text-[8px] bg-purple-100 text-purple-800 font-black">EM MONTAGEM</span>
                    </div>

                    <div className="text-[10px] text-slate-500 space-y-1">
                      <p>Prazo: {new Date(l.data_inicio_prevista).toLocaleDateString("pt-BR")} a {new Date(l.data_entrega_prevista).toLocaleDateString("pt-BR")}</p>
                      <p className="font-bold text-slate-700">{l.total_itens} SKUs • {l.total_pecas} peças</p>
                    </div>

                    <button
                      onClick={() => {
                        handleSelectLote(l.id);
                        setActiveSubTab("lotes");
                      }}
                      className="w-full text-center text-[10px] font-black uppercase tracking-wider bg-white border border-slate-200 text-slate-600 hover:bg-slate-100 p-1.5 rounded-lg transition-colors flex items-center justify-center gap-1"
                    >
                      <Eye size={12} /> Detalhar e Lançar
                    </button>
                  </div>
                ))}

                {lotes.filter(l => l.status === "rascunho").length === 0 && (
                  <div className="col-span-full py-8 text-center text-xs text-slate-400">
                    Nenhum lote em montagem no momento. Comece analisando SKUs e crie um lote!
                  </div>
                )}
              </div>
              </div>

            {/* New Row: Sent Batches Chart & Sent Batches List */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
              
              {/* Sent Batches Chart */}
              <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm lg:col-span-2 flex flex-col justify-between">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                    <TrendingUp size={15} className="text-purple-600" />
                    Peças Enviadas para a Fábrica (Linha do Tempo)
                  </h3>
                  <span className="text-[10px] font-bold text-slate-400 uppercase">Total Enviado: {lotes.filter(l => l.status !== "rascunho").reduce((acc, l) => acc + (l.total_pecas || 0), 0)} pçs</span>
                </div>
                
                <div className="flex-1 min-h-[260px]">
                  {sentBatchesChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={sentBatchesChartData} margin={{ top: 10, right: 10, left: -20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                        <XAxis dataKey="date" stroke="#94A3B8" fontSize={10} tickLine={false} />
                        <YAxis stroke="#94A3B8" fontSize={10} tickLine={false} />
                        <Tooltip contentStyle={{ background: "#1E293B", border: "none", borderRadius: "8px", color: "white", fontSize: "11px" }} />
                        <Bar
                          dataKey="Peças Enviadas"
                          fill="#8B5CF6"
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center text-xs text-slate-400 py-12">
                      <TrendingUp size={36} className="text-slate-300 mb-2 animate-bounce" />
                      Nenhum lote enviado ainda. Os lotes enviados aparecerão aqui ao longo do tempo.
                    </div>
                  )}
                </div>
              </div>

              {/* Sent Batches List */}
              <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
                <div className="flex justify-between items-center mb-4 border-b pb-2">
                  <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Últimos Lotes Enviados</h3>
                  <button 
                    onClick={() => {
                      setActiveSubTab("lotes");
                    }}
                    className="text-[10px] font-black text-blue-600 uppercase hover:underline"
                  >
                    Ver Todos
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto max-h-[250px] space-y-2.5 pr-1">
                  {lotes.filter(l => l.status !== "rascunho").slice(0, 10).length > 0 ? (
                    lotes.filter(l => l.status !== "rascunho").slice(0, 10).map(l => (
                      <div key={l.id} className="flex justify-between items-center text-xs border-b border-slate-100 pb-2 last:border-0">
                        <div>
                          <strong className="text-slate-800 block text-[11px] uppercase truncate max-w-[150px]">{l.nome}</strong>
                          <span className="text-[10px] text-slate-400">Enviado: {new Date(l.enviado_em || l.updated || l.created).toLocaleDateString("pt-BR")}</span>
                        </div>
                        <div className="text-right">
                          <span className="font-black text-purple-600 block">{l.total_pecas || 0} pçs</span>
                          <span className="text-[10px] text-slate-400">{l.total_itens || 0} SKUs</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="h-full flex items-center justify-center text-xs text-slate-400 py-12">
                      Nenhum lote enviado anteriormente.
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>
        )}

        {/* ========================================== */}
        {/* SUBTAB 2: ANÁLISE DE SKUs */}
        {/* ========================================== */}
        {activeSubTab === "analise" && (
          <div className="space-y-6 animate-fade-in">
            
            {/* Filter Configuration Panel */}
            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-4">
              <div className="flex items-center gap-2 border-b pb-3 mb-2">
                <Filter size={16} className="text-blue-600" />
                <h3 className="text-xs font-black text-slate-700 uppercase tracking-wider">Parâmetros de Filtros e Mês Alvo</h3>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                
                {/* Month Picker */}
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">Mês de Referência (Alvo):</label>
                  <select
                    className="w-full p-2 border rounded-lg text-xs font-bold text-slate-700 bg-slate-50 focus:ring-2 focus:ring-blue-500 outline-none"
                    value={targetMonth}
                    onChange={(e) => setTargetMonth(Number(e.target.value))}
                  >
                    {MONTH_NAMES.slice(1).map((m, idx) => (
                      <option key={m} value={idx + 1}>{m}</option>
                    ))}
                  </select>
                </div>

                {/* Categories */}
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">Categoria:</label>
                  <select
                    className="w-full p-2 border rounded-lg text-xs text-slate-600 bg-slate-50 focus:ring-2 focus:ring-blue-500 outline-none"
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                  >
                    <option value="TODOS">TODAS</option>
                    {PLANNING_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>

                {/* Jewelry Type */}
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">Tipo de Joia:</label>
                  <select
                    className="w-full p-2 border rounded-lg text-xs text-slate-600 bg-slate-50 focus:ring-2 focus:ring-blue-500 outline-none"
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                  >
                    <option value="TODOS">TODOS</option>
                    <option value="Anel">Anel</option>
                    <option value="Brinco">Brinco</option>
                    <option value="Pingente">Pingente</option>
                    <option value="Pulseira">Pulseira</option>
                    <option value="Corrente">Corrente</option>
                    <option value="Outros">Outros</option>
                  </select>
                </div>

                {/* Differentiator */}
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">Banho/Diferenciador:</label>
                  <select
                    className="w-full p-2 border rounded-lg text-xs text-slate-600 bg-slate-50 focus:ring-2 focus:ring-blue-500 outline-none"
                    value={filterDifferentiator}
                    onChange={(e) => setFilterDifferentiator(e.target.value)}
                  >
                    <option value="TODOS">TODOS</option>
                    <option value="Dourado">Dourado</option>
                    <option value="Ródio">Ródio</option>
                    <option value="Prata">Prata</option>
                    <option value="Outro/Nenhum">Outro/Nenhum</option>
                  </select>
                </div>

                {/* Aro/Ring size */}
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">Medida/Aro:</label>
                  <select
                    className="w-full p-2 border rounded-lg text-xs text-slate-600 bg-slate-50 focus:ring-2 focus:ring-blue-500 outline-none"
                    value={filterAro}
                    onChange={(e) => setFilterAro(e.target.value)}
                  >
                    <option value="TODOS">TODOS</option>
                    {stockSizesAvailable.map(s => <option key={s} value={s}>Aro {s}</option>)}
                  </select>
                </div>

                {/* Search SKU */}
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">Buscar SKU:</label>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 text-slate-400" size={14} />
                    <input
                      type="text"
                      className="w-full pl-8 pr-2 py-1.5 border rounded-lg text-xs bg-slate-50 focus:ring-2 focus:ring-blue-500 outline-none"
                      placeholder="Ex: ALI-01"
                      value={filterSku}
                      onChange={(e) => setFilterSku(e.target.value)}
                    />
                  </div>
                </div>

                {/* Search Name */}
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">Buscar Nome:</label>
                  <input
                    type="text"
                    className="w-full px-3 py-1.5 border rounded-lg text-xs bg-slate-50 focus:ring-2 focus:ring-blue-500 outline-none"
                    placeholder="Ex: Aliança Ouro"
                    value={filterName}
                    onChange={(e) => setFilterName(e.target.value)}
                  />
                </div>

                {/* Course or saint tag */}
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">Curso / Santo / Medalha:</label>
                  <input
                    type="text"
                    className="w-full px-3 py-1.5 border rounded-lg text-xs bg-slate-50 focus:ring-2 focus:ring-blue-500 outline-none"
                    placeholder="Ex: Direito, Fátima"
                    value={filterCourseSaint}
                    onChange={(e) => setFilterCourseSaint(e.target.value)}
                  />
                </div>

                {/* Situation */}
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">Situação Estoque:</label>
                  <select
                    className="w-full p-2 border rounded-lg text-xs text-slate-600 bg-slate-50 focus:ring-2 focus:ring-blue-500 outline-none"
                    value={filterSituation}
                    onChange={(e) => setFilterSituation(e.target.value)}
                  >
                    <option value="TODOS">TODAS</option>
                    <option value="OK">OK (Coberto)</option>
                    <option value="ABAIXO">Abaixo do Mínimo</option>
                    <option value="SEM ESTOQUE">Sem Estoque</option>
                  </select>
                </div>

                {/* Minimum Stock State defined */}
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">Mínimo Definido:</label>
                  <select
                    className="w-full p-2 border rounded-lg text-xs text-slate-600 bg-slate-50 focus:ring-2 focus:ring-blue-500 outline-none"
                    value={filterMinDef}
                    onChange={(e) => setFilterMinDef(e.target.value)}
                  >
                    <option value="TODOS">TODOS</option>
                    <option value="DEFINIDO">Com Mínimo Definido</option>
                    <option value="SEM_DEFINIR">Sem Mínimo Definido (Null)</option>
                  </select>
                </div>

                {/* Status na Fábrica (Em Produção) */}
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">Status na Fábrica:</label>
                  <select
                    className="w-full p-2 border rounded-lg text-xs text-slate-600 bg-slate-50 focus:ring-2 focus:ring-blue-500 outline-none"
                    value={filterFactoryStatus}
                    onChange={(e) => setFilterFactoryStatus(e.target.value)}
                  >
                    <option value="TODOS">TODOS</option>
                    <option value="EM_PRODUCAO">Em Produção na Fábrica</option>
                    <option value="SEM_PRODUCAO">Fora de Produção (Sem Fábrica)</option>
                  </select>
                </div>

              </div>

              {/* Form buttons */}
              <div className="flex justify-between items-center pt-2">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={filterWithHistoryOnly}
                    onChange={(e) => setFilterWithHistoryOnly(e.target.checked)}
                    className="rounded border-slate-350 text-blue-600 focus:ring-blue-500 w-4 h-4"
                  />
                  <span className="text-xs font-bold text-slate-650 uppercase">Apenas com Histórico de Vendas (Urgentes, Críticos, Alto, Médio, OK)</span>
                </label>
                <button
                  onClick={handleApplyFilters}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-black shadow-md shadow-blue-100 flex items-center gap-1.5 uppercase tracking-wider transition-all"
                >
                  <RefreshCw size={14} /> Aplicar Filtros e Calcular
                </button>
              </div>

            </div>

            {/* Mass actions and allocations panel */}
            {hasLoadedItems && (
              <div className="bg-slate-100 border border-slate-200 p-4 rounded-xl flex flex-wrap items-center justify-between gap-4">
                
                <div className="flex items-center gap-2">
                  <span className="text-xs font-black text-slate-600 uppercase">
                    {selectedSkus.size} itens selecionados para lote
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  
                  {/* Select batch and allocate */}
                  <select
                    className="p-2 border rounded-lg text-xs bg-white font-bold text-slate-700 outline-none"
                    disabled={selectedSkus.size === 0}
                    onChange={(e) => {
                      if (e.target.value === "NEW") {
                        setShowCreateLoteModal(true);
                      } else if (e.target.value) {
                        handleAllocateSelectedToLote(e.target.value);
                      }
                      e.target.value = "";
                    }}
                  >
                    <option value="">Adicionar ao Lote...</option>
                    {lotes.filter(l => l.status === "rascunho").map(l => (
                      <option key={l.id} value={l.id}>{l.nome} ({l.total_pecas} pçs)</option>
                    ))}
                    <option value="NEW" className="text-blue-600 font-bold border-t">+ Criar Novo Lote...</option>
                  </select>

                  {/* Clear selection */}
                  {selectedSkus.size > 0 && (
                    <button
                      onClick={() => setSelectedSkus(new Set())}
                      className="text-xs text-slate-500 hover:text-slate-700 underline font-semibold"
                    >
                      Limpar Seleção
                    </button>
                  )}

                </div>

              </div>
            )}

            {/* Simulated items table list */}
            {hasLoadedItems ? (
              <>
                <div className="border rounded-xl overflow-hidden bg-white shadow-sm">
                <table className="w-full text-left text-xs border-collapse">
                  <thead className="bg-slate-50 text-slate-500 font-bold uppercase border-b border-slate-200">
                    <tr>
                      <th className="p-3 w-10">
                        <input
                          type="checkbox"
                          checked={isAllSelected}
                          onChange={(e) => handleSelectAllVisible(e.target.checked)}
                          className="rounded text-blue-600 w-4 h-4 cursor-pointer"
                        />
                      </th>
                      <th className="p-3 w-8"></th>
                      <th className="p-3">Prioridade</th>
                      <th className="p-3">SKU</th>
                      <th className="p-3">Nome do Produto</th>
                      <th className="p-3 text-center">Vendas 6m</th>
                      <th className="p-3 text-center">Média Mensal</th>
                      <th className="p-3 text-center">Estoque Atual</th>
                      <th className="p-3 text-center">Mínimo Definido</th>
                      <th className="p-3 text-center">Mínimo Sugerido</th>
                      <th className="p-3 text-center">Lacuna / Sugestão</th>
                      <th className="p-3 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedItems.map((item) => {
                      const isExpanded = expandedRings.has(item.sku);
                      
                      // Ring group row
                      if (item.isGroup) {
                        return (
                          <React.Fragment key={item.sku}>
                            <tr className="bg-slate-100/80 hover:bg-slate-150 border-b border-slate-200 font-bold">
                              <td className="p-3 text-center">
                                <input
                                  type="checkbox"
                                  checked={isGroupSelected(item)}
                                  onChange={(e) => handleToggleGroupSelection(item, e.target.checked)}
                                  className="rounded text-blue-600 w-4 h-4 cursor-pointer"
                                />
                              </td>
                              <td className="p-3 text-center">
                                <button
                                  onClick={() => {
                                    setExpandedRings(prev => {
                                      const next = new Set(prev);
                                      if (next.has(item.sku)) next.delete(item.sku);
                                      else next.add(item.sku);
                                      return next;
                                    });
                                  }}
                                  className="p-1 hover:bg-slate-250 rounded text-slate-500 transition-transform"
                                  style={{ transform: isExpanded ? "rotate(180deg)" : "none" }}
                                >
                                  <ChevronDown size={14} />
                                </button>
                              </td>
                              <td className="p-3"></td>
                              <td className="p-3 font-mono text-slate-800">{item.sku}</td>
                              <td className="p-3 text-slate-700">
                                {item.produto_nome} [Grupo Anéis]
                                {(() => {
                                  const gBatches = getGroupAllocatedBatches(item);
                                  const gProdCount = item.items.reduce((acc, sub) => acc + (factoryProductionSkus[sub.sku.toUpperCase().trim()] || 0), 0);
                                  
                                  return (
                                    <>
                                      {gBatches.length > 0 && (
                                        <span className="text-[10px] text-purple-650 bg-purple-50 border border-purple-200 rounded px-1.5 py-0.5 ml-2 font-bold whitespace-nowrap">
                                          Lotes ativos: {gBatches.join(", ")}
                                        </span>
                                      )}
                                      {gProdCount > 0 && (
                                        <span className="text-[10px] text-teal-650 bg-teal-50 border border-teal-200 rounded px-1.5 py-0.5 ml-2 font-bold whitespace-nowrap">
                                          Fábrica: {gProdCount} pçs
                                        </span>
                                      )}
                                    </>
                                  );
                                })()}
                              </td>
                              <td className="p-3 text-center">{item.vendas_6_meses}</td>
                              <td className="p-3 text-center">
                                {Math.round((item.vendas_6_meses / (configs.janela_historico_meses || 6)) * 10) / 10}
                              </td>
                              <td className="p-3 text-center">{item.estoque_atual}</td>
                              <td className="p-3 text-center font-bold text-slate-700">
                                {item.estoque_minimo_definido || "-"}
                              </td>
                              <td className="p-3 text-center text-slate-400">
                                {item.estoque_minimo_sugerido}
                              </td>
                              <td className="p-3 text-center">
                                {item.lacuna > 0 ? (
                                  <span className="text-red-500 font-black">+{item.lacuna} (sug. {item.quantidade_sugerida})</span>
                                ) : (
                                  <span className="text-emerald-600">Suficiente</span>
                                )}
                              </td>
                              <td className="p-3"></td>
                            </tr>

                            {/* Ring variations nested list */}
                            {isExpanded && item.items.map(subItem => {
                              const minVal = subItem.estoque_minimo_definido !== null ? subItem.estoque_minimo_definido : subItem.estoque_minimo_sugerido;
                              
                              return (
                                <tr key={subItem.sku} className="border-b bg-slate-50/50 hover:bg-slate-100/60 border-slate-200">
                                  <td className="p-2.5 text-center">
                                    <input
                                      type="checkbox"
                                      checked={selectedSkus.has(subItem.sku)}
                                      onChange={(e) => handleToggleSkuSelection(subItem.sku, e.target.checked)}
                                      className="rounded text-blue-600 w-3.5 h-3.5 cursor-pointer"
                                    />
                                  </td>
                                  <td></td>
                                  <td className="p-2.5">
                                    <span className={`px-2 py-0.5 rounded text-[8px] border font-black uppercase tracking-wider ${PRIORITY_CLASSES[subItem.prioridade]}`}>
                                      {PRIORITY_LABELS[subItem.prioridade]}
                                    </span>
                                  </td>
                                  <td className="p-2.5 font-mono text-slate-650 pl-6">
                                    — Aro {subItem.ringSize}
                                  </td>
                                  <td className="p-2.5 text-slate-500 text-[10px] font-mono">
                                    {subItem.sku}
                                    {allocatedSkusMap[subItem.sku.toUpperCase().trim()] && (
                                      <span className="text-[9px] text-purple-600 bg-purple-50 border border-purple-100 rounded px-1 ml-2 font-bold whitespace-nowrap">
                                        Lote: {allocatedSkusMap[subItem.sku.toUpperCase().trim()].join(", ")}
                                      </span>
                                    )}
                                    {factoryProductionSkus[subItem.sku.toUpperCase().trim()] && (
                                      <span className="text-[9px] text-teal-650 bg-teal-50 border border-teal-150 rounded px-1 ml-2 font-bold whitespace-nowrap">
                                        Fábrica: {factoryProductionSkus[subItem.sku.toUpperCase().trim()]} pçs
                                      </span>
                                    )}
                                  </td>
                                  <td className="p-2.5 text-center font-mono">{subItem.vendas_6_meses}</td>
                                  <td className="p-2.5 text-center font-mono">{subItem.media_mensal}</td>
                                  <td className="p-2.5 text-center font-black text-slate-600 bg-slate-100/30">{subItem.estoque_atual}</td>
                                  <td className="p-2.5 text-center font-black">
                                    {subItem.estoque_minimo_definido !== null ? (
                                      <span className="text-slate-800">{subItem.estoque_minimo_definido}</span>
                                    ) : (
                                      <span className="text-slate-300 font-normal italic">Não def.</span>
                                    )}
                                  </td>
                                  <td className="p-2.5 text-center text-slate-400 font-mono">{subItem.estoque_minimo_sugerido}</td>
                                  <td className="p-2.5 text-center font-mono">
                                    {subItem.lacuna > 0 ? (
                                      <span className="text-red-500 font-extrabold">+{subItem.lacuna} (sug. {subItem.quantidade_sugerida})</span>
                                    ) : (
                                      <span className="text-emerald-700">OK</span>
                                    )}
                                  </td>
                                  <td className="p-2.5 text-right">
                                    <button
                                      onClick={() => {
                                        setEditingItemForMin(subItem);
                                        setNewMinVal(subItem.estoque_minimo_definido !== null ? String(subItem.estoque_minimo_definido) : "");
                                      }}
                                      className="text-blue-500 hover:text-blue-700 text-[10px] font-black uppercase hover:underline"
                                    >
                                      Definir Mínimo
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        );
                      }

                      // Standard row
                      const minVal = item.estoque_minimo_definido !== null ? item.estoque_minimo_definido : item.estoque_minimo_sugerido;

                      return (
                        <tr key={item.sku} className="border-b hover:bg-slate-50">
                          <td className="p-3 text-center">
                            <input
                              type="checkbox"
                              checked={selectedSkus.has(item.sku)}
                              onChange={(e) => handleToggleSkuSelection(item.sku, e.target.checked)}
                              className="rounded text-blue-600 w-4 h-4 cursor-pointer"
                            />
                          </td>
                          <td></td>
                          <td className="p-3">
                            <span className={`px-2 py-0.5 rounded text-[8px] border font-black uppercase tracking-wider ${PRIORITY_CLASSES[item.prioridade]}`}>
                              {PRIORITY_LABELS[item.prioridade]}
                            </span>
                          </td>
                          <td className="p-3 font-mono font-bold text-slate-800">{item.sku}</td>
                          <td className="p-3 text-slate-700 font-medium">
                            {item.produto_nome}
                            {allocatedSkusMap[item.sku.toUpperCase().trim()] && (
                              <span className="text-[10px] text-purple-600 bg-purple-50 border border-purple-155 rounded px-1.5 py-0.5 ml-2 font-bold whitespace-nowrap">
                                Lote: {allocatedSkusMap[item.sku.toUpperCase().trim()].join(", ")}
                              </span>
                            )}
                            {factoryProductionSkus[item.sku.toUpperCase().trim()] && (
                              <span className="text-[10px] text-teal-650 bg-teal-50 border border-teal-155 rounded px-1.5 py-0.5 ml-2 font-bold whitespace-nowrap">
                                Fábrica: {factoryProductionSkus[item.sku.toUpperCase().trim()]} pçs
                              </span>
                            )}
                          </td>
                          <td className="p-3 text-center">{item.vendas_6_meses}</td>
                          <td className="p-3 text-center font-mono">{item.media_mensal}</td>
                          <td className="p-3 text-center font-black text-slate-600 bg-slate-50/50">{item.estoque_atual}</td>
                          <td className="p-3 text-center font-bold">
                            {item.estoque_minimo_definido !== null ? (
                              <span className="text-slate-800">{item.estoque_minimo_definido}</span>
                            ) : (
                              <span className="text-slate-300 font-normal italic">Não def.</span>
                            )}
                          </td>
                          <td className="p-3 text-center text-slate-400 font-mono">{item.estoque_minimo_sugerido}</td>
                          <td className="p-3 text-center font-mono">
                            {item.lacuna > 0 ? (
                              <span className="text-red-500 font-black">+{item.lacuna} (sug. {item.quantidade_sugerida})</span>
                            ) : (
                              <span className="text-emerald-700">OK</span>
                            )}
                          </td>
                          <td className="p-3 text-right">
                            <button
                              onClick={() => {
                                setEditingItemForMin(item);
                                setNewMinVal(item.estoque_minimo_definido !== null ? String(item.estoque_minimo_definido) : "");
                              }}
                              className="text-blue-500 hover:text-blue-700 text-[10px] font-black uppercase hover:underline"
                            >
                              Definir Mínimo
                            </button>
                          </td>
                        </tr>
                      );
                    })}

                    {groupedPlanningItems.length === 0 && (
                      <tr>
                        <td colSpan="12" className="p-8 text-center text-slate-400 font-medium">
                          Nenhum SKU encontrado com os filtros e busca aplicados.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-slate-200 pt-4 px-1 shrink-0 bg-white">
                  <div className="flex items-center gap-2 text-xs font-bold text-slate-500">
                    <span>Itens por página:</span>
                    <select
                      className="p-1 border rounded bg-white text-slate-700 font-bold outline-none cursor-pointer text-xs"
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={200}>200</option>
                      <option value={500}>500</option>
                    </select>
                  </div>

                  <div className="text-xs text-slate-500 font-medium font-bold">
                    Mostrando <span className="font-black text-slate-800">{Math.min(groupedPlanningItems.length, (currentPage - 1) * itemsPerPage + 1)}-{Math.min(groupedPlanningItems.length, currentPage * itemsPerPage)}</span> de <span className="font-black text-slate-800">{groupedPlanningItems.length}</span> SKUs
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1.5 border rounded-lg text-xs font-bold text-slate-600 bg-white hover:bg-slate-50 disabled:opacity-50 disabled:pointer-events-none transition-all shadow-sm"
                    >
                      Anterior
                    </button>
                    <span className="text-xs text-slate-650 font-bold px-2">
                      Página {currentPage} de {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1.5 border rounded-lg text-xs font-bold text-slate-600 bg-white hover:bg-slate-50 disabled:opacity-50 disabled:pointer-events-none transition-all shadow-sm"
                    >
                      Próxima
                    </button>
                  </div>
                </div>
              )}

            </>
          ) : (
              <div className="bg-white rounded-xl border border-dashed border-slate-300 p-12 text-center flex flex-col items-center justify-center">
                <List size={32} className="text-slate-400 mb-2" />
                <h4 className="text-sm font-bold text-slate-700">Carregar Análise de SKUs</h4>
                <p className="text-xs text-slate-400 max-w-sm mt-1">A lista de SKUs é carregada sob demanda para máxima eficiência. Configure o mês alvo e filtros e clique em "Aplicar Filtros e Calcular".</p>
              </div>
            )}

          </div>
        )}

        {/* ========================================== */}
        {/* SUBTAB 3: MEUS LOTES */}
        {/* ========================================== */}
        {activeSubTab === "lotes" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-full items-stretch animate-fade-in">
            
            {/* Left Column: Active Batches & History List */}
            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-col h-[600px] overflow-hidden">
              <div className="flex justify-between items-center pb-3 border-b mb-4 shrink-0">
                <h3 className="text-xs font-black text-slate-700 uppercase tracking-wider">Lotes</h3>
                <button
                  onClick={() => setShowCreateLoteModal(true)}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-750 text-white rounded-lg text-[10px] font-black uppercase tracking-wider flex items-center gap-1 transition-all"
                >
                  <Plus size={12} /> Novo Lote
                </button>
              </div>

              <div className="flex-1 overflow-y-auto space-y-5 min-h-0 pr-1">
                
                {/* Lotes em montagem section */}
                <div>
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block mb-2">Lotes em Montagem</span>
                  <div className="space-y-2.5">
                    {lotes.filter(l => l.status === "rascunho").map(l => (
                      <div
                        key={l.id}
                        onClick={() => handleSelectLote(l.id)}
                        className={`p-3 border rounded-xl cursor-pointer transition-all flex flex-col justify-between hover:border-purple-300 ${
                          selectedLoteId === l.id ? "bg-purple-50/50 border-purple-300 ring-2 ring-purple-100" : "bg-slate-50"
                        }`}
                      >
                        <div className="flex justify-between">
                          <strong className="text-xs text-slate-800 truncate font-bold max-w-[150px]">{l.nome}</strong>
                          <span className="px-1.5 py-0.5 rounded text-[8px] bg-purple-100 text-purple-800 font-bold uppercase">Rascunho</span>
                        </div>
                        <div className="flex justify-between items-center text-[10px] text-slate-500 mt-2 font-medium">
                          <span>{l.total_itens} SKUs • {l.total_pecas} peças</span>
                          <span>Início: {new Date(l.data_inicio_prevista).toLocaleDateString("pt-BR")}</span>
                        </div>
                      </div>
                    ))}
                    
                    {lotes.filter(l => l.status === "rascunho").length === 0 && (
                      <p className="text-[10px] text-slate-400 italic py-2">Nenhum lote em montagem.</p>
                    )}
                  </div>
                </div>

                {/* History lotes sent */}
                <div className="border-t pt-4">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block mb-2">Enviados / Histórico</span>
                  <div className="space-y-2">
                    {lotes.filter(l => l.status !== "rascunho").map(l => (
                      <div
                        key={l.id}
                        onClick={() => handleSelectLote(l.id)}
                        className={`p-3 border rounded-xl cursor-pointer transition-all flex flex-col justify-between hover:bg-slate-50 ${
                          selectedLoteId === l.id ? "bg-blue-50/50 border-blue-300" : "bg-slate-50/70 opacity-80"
                        }`}
                      >
                        <div className="flex justify-between">
                          <strong className="text-xs text-slate-700 truncate font-semibold max-w-[150px]">{l.nome}</strong>
                          <span className="px-1.5 py-0.5 rounded text-[8px] bg-emerald-100 text-emerald-800 font-bold uppercase">ENVIADO</span>
                        </div>
                        <div className="flex justify-between items-center text-[10px] text-slate-400 mt-2">
                          <span>{l.total_itens} SKUs • {l.total_pecas} peças</span>
                          <span>Enviado: {new Date(l.enviado_em || l.updated).toLocaleDateString("pt-BR")}</span>
                        </div>
                      </div>
                    ))}
                    
                    {lotes.filter(l => l.status !== "rascunho").length === 0 && (
                      <p className="text-[10px] text-slate-400 italic py-2">Nenhum lote enviado anteriormente.</p>
                    )}
                  </div>
                </div>

              </div>
            </div>

            {/* Right Column: Selected Batch Details & Workshop check */}
            <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm lg:col-span-2 flex flex-col h-[600px] overflow-hidden">
              {selectedLoteId ? (
                <div className="flex flex-col h-full overflow-hidden">
                  
                  {/* Batch Header */}
                  {(() => {
                    const lote = lotes.find(l => l.id === selectedLoteId);
                    if (!lote) return null;

                    const isDraft = lote.status === "rascunho";
                    
                    // Available Weekly capacity check
                    const availableCapacity = Math.max(0, workshopCapacity.weekly - workshopCapacity.peSolicitado);
                    const isOverCapacity = isDraft && (lote.total_pecas > availableCapacity);

                    return (
                      <div className="space-y-4 shrink-0 pb-4 border-b">
                        
                        <div className="flex justify-between items-start">
                          <div>
                            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Lote Selecionado</span>
                            <h3 className="text-sm font-black text-slate-800 uppercase">{lote.nome}</h3>
                          </div>
                          
                          <div className="flex gap-2">
                            {isDraft && (
                              <>
                                <button
                                  onClick={() => handleCancelLote(lote.id)}
                                  className="px-3 py-1.5 border border-red-200 text-red-650 hover:bg-red-50 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-colors"
                                >
                                  Excluir Lote
                                </button>
                                <button
                                  onClick={() => handleSendLoteToFactory(lote.id)}
                                  className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-[10px] font-black uppercase tracking-wider shadow flex items-center gap-1 transition-all"
                                >
                                  <Play size={10} /> Enviar p/ Fábrica
                                </button>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Editable start date */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-slate-50 p-3 rounded-xl border">
                          <div className="space-y-1">
                            <label className="block text-[9px] font-bold text-slate-400 uppercase tracking-wider">Data Início Prevista:</label>
                            <input
                              type="date"
                              className="w-full p-1 border rounded text-xs bg-white text-slate-700 outline-none focus:ring-1 focus:ring-blue-500 font-bold"
                              disabled={!isDraft}
                              value={lote.data_inicio_prevista.split("T")[0]}
                              onChange={(e) => handleUpdateLoteStartDate(lote.id, e.target.value)}
                            />
                          </div>

                          <div className="space-y-1">
                            <label className="block text-[9px] font-bold text-slate-400 uppercase tracking-wider">Entrega Prevista:</label>
                            <p className="text-xs font-bold text-slate-700 pt-1.5">
                              {new Date(lote.data_entrega_prevista).toLocaleDateString("pt-BR")}
                            </p>
                          </div>

                          <div className="space-y-1">
                            <label className="block text-[9px] font-bold text-slate-400 uppercase tracking-wider">Peças Totais:</label>
                            <p className="text-xs font-black text-slate-700 pt-1.5">
                              {lote.total_itens} SKUs • {lote.total_pecas} peças
                            </p>
                          </div>
                        </div>

                        {/* Mini Dash de Carga da Oficina com Configuração Inline */}
                        {(() => {
                          const totalPecasLote = lote.total_pecas || 0;
                          const peSolicitado = workshopCapacity.peSolicitado || 0;
                          const capacity = workshopCapacity.weekly || 67;

                          const overrideVal = Number(configs.oficina_capacidade_semanal);
                          let capacityType = "default";
                          if (overrideVal && overrideVal > 0) {
                            capacityType = "override";
                          } else if (calculated7dCapacity && calculated7dCapacity > 0) {
                            capacityType = "calculated";
                          }

                          let existingLoad = 0;
                          let currentBatchLoad = 0;
                          let totalProjected = 0;

                          if (isDraft) {
                            existingLoad = peSolicitado;
                            currentBatchLoad = totalPecasLote;
                            totalProjected = peSolicitado + totalPecasLote;
                          } else {
                            currentBatchLoad = totalPecasLote;
                            existingLoad = Math.max(0, peSolicitado - totalPecasLote);
                            totalProjected = peSolicitado;
                          }

                          const pctExisting = Math.min(100, (existingLoad / capacity) * 100);
                          const pctLote = Math.min(100 - pctExisting, (currentBatchLoad / capacity) * 100);
                          const totalPct = (totalProjected / capacity) * 100;
                          const isExceeded = totalProjected > capacity;

                          return (
                            <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200 space-y-3">
                              <div className="flex justify-between items-center">
                                <div className="flex items-center gap-1.5">
                                  <TrendingUp size={14} className="text-slate-500" />
                                  <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Carga da Oficina (Capacidade Total)</span>
                                </div>
                                
                                {/* Configuração/Override de Capacidade */}
                                <div className="flex items-center gap-2 text-xs">
                                  <span className="text-slate-400 font-medium text-[10px] uppercase">Capacidade:</span>
                                  {isEditingCapacity ? (
                                    <div className="flex items-center gap-1 bg-white border rounded p-0.5 shadow-sm">
                                      <input
                                        type="number"
                                        min="0"
                                        placeholder="0 para usar calculada"
                                        className="w-12 p-0.5 text-center font-mono font-bold text-xs text-slate-800 outline-none"
                                        value={tempCapacity}
                                        onChange={(e) => setTempCapacity(e.target.value)}
                                      />
                                      <button
                                        onClick={handleSaveCapacityOverride}
                                        className="p-0.5 text-emerald-600 hover:text-emerald-700 transition-colors"
                                        title="Salvar"
                                      >
                                        <Check size={12} />
                                      </button>
                                      <button
                                        onClick={() => setIsEditingCapacity(false)}
                                        className="p-0.5 text-red-500 hover:text-red-650 transition-colors"
                                        title="Cancelar"
                                      >
                                        <X size={12} />
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-1.5">
                                      <div className="flex flex-col items-end">
                                        <strong className="font-mono font-black text-slate-800">{capacity} peças</strong>
                                        <span className={`text-[8px] font-black uppercase px-1 rounded shrink-0 ${
                                          capacityType === "override" 
                                            ? "bg-blue-100 text-blue-700" 
                                            : capacityType === "calculated"
                                              ? "bg-emerald-100 text-emerald-800"
                                              : "bg-slate-100 text-slate-650"
                                        }`}>
                                          {capacityType === "override" 
                                            ? "Override Manual" 
                                            : capacityType === "calculated"
                                              ? "Calculada (6x 7d)"
                                              : "Padrão"
                                          }
                                        </span>
                                      </div>
                                      <button
                                        onClick={() => {
                                          setTempCapacity(configs.oficina_capacidade_semanal ? String(configs.oficina_capacidade_semanal) : "");
                                          setIsEditingCapacity(true);
                                        }}
                                        className="text-blue-600 hover:text-blue-700 font-black flex items-center gap-0.5 text-[9px] uppercase tracking-wider transition-colors border border-blue-200 px-1 py-0.5 rounded hover:bg-blue-50"
                                        title="Alterar capacidade"
                                      >
                                        <Edit2 size={8} /> Override
                                      </button>
                                      {capacityType === "override" && (
                                        <button
                                          onClick={async () => {
                                            setLoading(true);
                                            setLoadingMsg("Removendo override...");
                                            try {
                                              await planningService.updateConfig(configs.id, {
                                                prazo_fabricacao_dias: Number(configs.prazo_fabricacao_dias),
                                                buffer_seguranca_percentual: Number(configs.buffer_seguranca_percentual),
                                                janela_historico_meses: Number(configs.janela_historico_meses),
                                                oficina_capacidade_semanal: 0,
                                              }, user?.id);
                                              setConfigs(prev => ({ ...prev, oficina_capacidade_semanal: 0 }));
                                              showNotification("Override removido! Usando capacidade calculada.");
                                            } catch (e) {
                                              console.error(e);
                                              showNotification("Erro ao remover override.", "error");
                                            } finally {
                                              setLoading(false);
                                            }
                                          }}
                                          className="text-red-500 hover:text-red-700 font-black text-[9px] uppercase tracking-wider border border-red-200 px-1 py-0.5 rounded hover:bg-red-50"
                                          title="Limpar override e usar capacidade calculada"
                                        >
                                          Limpar
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Stacked Progress Bar */}
                              <div className="w-full bg-slate-200 h-3 rounded-full flex overflow-hidden border shadow-inner">
                                {/* Fila Existente (pe_solicitado) */}
                                {existingLoad > 0 && (
                                  <div 
                                    className="bg-emerald-500 h-full transition-all duration-500 cursor-help"
                                    style={{ width: `${pctExisting}%` }}
                                    title={`Fila da Oficina: ${existingLoad} peças (${(existingLoad / capacity * 100).toFixed(0)}%)`}
                                  ></div>
                                )}
                                {/* Este Lote (lote.total_pecas) */}
                                {currentBatchLoad > 0 && (
                                  <div 
                                    className={`h-full transition-all duration-500 cursor-help ${
                                      isExceeded && isDraft ? "bg-red-500 animate-pulse" : "bg-blue-600"
                                    }`}
                                    style={{ width: `${pctLote}%` }}
                                    title={`Este Lote: ${currentBatchLoad} peças (${(currentBatchLoad / capacity * 100).toFixed(0)}%)`}
                                  ></div>
                                )}
                              </div>

                              {/* Legend & Stats */}
                              <div className="flex flex-wrap items-center justify-between gap-y-2 gap-x-4 text-[9px] text-slate-500 uppercase tracking-wider">
                                <div className="flex items-center gap-3">
                                  <div className="flex items-center gap-1">
                                    <span className="w-2 h-2 rounded bg-emerald-500 shrink-0"></span>
                                    <span>Fila Oficina: <strong className="font-mono font-bold text-slate-700">{existingLoad} pçs</strong> ({((existingLoad / capacity) * 100).toFixed(0)}%)</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <span className="w-2 h-2 rounded bg-blue-600 shrink-0"></span>
                                    <span>Este Lote: <strong className="font-mono font-bold text-slate-700">{currentBatchLoad} pçs</strong> ({((currentBatchLoad / capacity) * 100).toFixed(0)}%)</span>
                                  </div>
                                </div>
                                
                                <div>
                                  Carga Total: <strong className={`font-mono font-black ${isExceeded ? "text-red-650" : "text-slate-800"}`}>{totalProjected} / {capacity} pçs</strong> (<strong className={isExceeded ? "text-red-650 animate-pulse font-black" : "text-slate-800"}>{totalPct.toFixed(0)}%</strong>)
                                </div>
                              </div>

                              {/* Exceeded Warning Banner */}
                              {isExceeded && isDraft && (
                                <div className="p-2 bg-red-100 text-red-700 border border-red-300 rounded-lg text-[10px] font-bold flex items-center gap-1.5 animate-pulse">
                                  <AlertTriangle size={12} className="text-red-500 shrink-0" />
                                  <span>Carga projetada excede a capacidade semanal em {totalProjected - capacity} peças! Ajuste as quantidades ou adie itens.</span>
                                </div>
                              )}
                            </div>
                          );
                        })()}

                      </div>
                    );
                  })()}

                  {/* Form de Inclusão Manual de SKU ao Lote */}
                  {(() => {
                    const lote = lotes.find(l => l.id === selectedLoteId);
                    const isDraft = lote && lote.status === "rascunho";
                    if (!isDraft) return null;

                    return (
                      <div className="bg-purple-50/80 p-3 rounded-xl border border-purple-200 mb-3 flex flex-col sm:flex-row items-center justify-between gap-3 shrink-0">
                        <span className="text-xs font-bold text-purple-900 flex items-center gap-1.5 shrink-0">
                          <Plus size={14} className="text-purple-600" /> Adicionar SKU Manualmente a este Lote:
                        </span>
                        <div className="flex items-center gap-2 w-full sm:w-auto flex-1 max-w-md">
                          <input
                            type="text"
                            placeholder="SKU da peça (ex: DIR-AUR-Z-14)"
                            className="flex-1 bg-white border border-purple-200 rounded-lg px-2.5 py-1.5 text-xs font-mono font-bold uppercase outline-none focus:border-purple-500 text-slate-800"
                            value={manualAddSku}
                            onChange={(e) => setManualAddSku(e.target.value)}
                          />
                          <input
                            type="number"
                            min="1"
                            placeholder="Qtd"
                            className="w-16 bg-white border border-purple-200 rounded-lg px-2 py-1.5 text-xs font-bold text-center outline-none focus:border-purple-500 text-slate-800"
                            value={manualAddQty}
                            onChange={(e) => setManualAddQty(e.target.value)}
                          />
                          <button
                            type="button"
                            onClick={async () => {
                              if (!selectedLoteId || !manualAddSku.trim()) {
                                alert("Digite um SKU válido.");
                                return;
                              }
                              try {
                                setLoading(true);
                                const updated = await planningService.addManualItemToLote(selectedLoteId, manualAddSku.trim(), manualAddQty);
                                setSelectedLoteItems(updated);
                                setManualAddSku("");
                                setManualAddQty(1);
                                showNotification("SKU adicionado ao lote com sucesso!");
                                // Reload lotes summary
                                const updatedLotes = await planningService.getLotes();
                                setLotes(updatedLotes);
                              } catch (e) {
                                alert("Erro ao adicionar SKU ao lote: " + e.message);
                              } finally {
                                setLoading(false);
                              }
                            }}
                            className="px-3.5 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs font-bold shadow-xs whitespace-nowrap cursor-pointer"
                          >
                            + Incluir
                          </button>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Batch Items Table */}
                  <div className="flex-1 overflow-y-auto min-h-0 pt-2">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead className="bg-slate-50 text-slate-500 font-bold uppercase border-b sticky top-0 z-10">
                        <tr>
                          <th className="pb-2">Prioridade</th>
                          <th className="pb-2">SKU</th>
                          <th className="pb-2">Nome / Tamanho</th>
                          <th className="pb-2 text-center">Estoque</th>
                          <th className="pb-2 text-center">Mínimo</th>
                          <th className="pb-2 text-center w-24">Qtd Aprovada</th>
                          <th className="pb-2 text-right">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedLoteItems.map(item => {
                          const lote = lotes.find(l => l.id === selectedLoteId);
                          const isDraft = lote && lote.status === "rascunho";
                          
                          return (
                            <tr key={item.sku} className="border-b last:border-0 hover:bg-slate-50/50">
                              <td className="py-2.5">
                                {isDraft ? (
                                  <select
                                    value={item.prioridade || "Planejado"}
                                    onChange={(e) => handleUpdateLoteItemPriority(item.id, e.target.value)}
                                    className={`px-1 py-0.5 border rounded text-[10px] font-bold uppercase tracking-wider outline-none bg-white cursor-pointer ${
                                      USER_PRIORITY_CLASSES[item.prioridade || "Planejado"]
                                    }`}
                                  >
                                    <option value="Planejado">Planejado</option>
                                    <option value="Prioritario">Prioritário</option>
                                    <option value="Urgente">Urgente</option>
                                  </select>
                                ) : (
                                  <span className={`px-1.5 py-0.5 rounded text-[8px] border font-black uppercase tracking-wider ${USER_PRIORITY_CLASSES[item.prioridade || "Planejado"]}`}>
                                    {item.prioridade || "Planejado"}
                                  </span>
                                )}
                              </td>
                              <td className="py-2.5 font-mono font-bold text-slate-800">{item.sku}</td>
                              <td className="py-2.5 text-slate-600 truncate max-w-[180px]" title={item.produto_nome}>
                                {item.produto_nome}
                              </td>
                              <td className="py-2.5 text-center font-mono">{item.estoque_atual}</td>
                              <td className="py-2.5 text-center font-mono">{item.estoque_minimo}</td>
                              <td className="py-2.5 text-center">
                                <input
                                  type="number"
                                  min="0"
                                  className="w-16 p-1 border rounded text-center font-mono font-bold text-slate-800 bg-white"
                                  disabled={!isDraft}
                                  value={item.quantidade_aprovada}
                                  onChange={(e) => handleUpdateLoteItemQty(item.id, e.target.value)}
                                />
                              </td>
                              <td className="py-2.5 text-right">
                                {isDraft && (
                                  <button
                                    onClick={() => handleRemoveLoteItem(item.id)}
                                    className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                                    title="Remover do lote"
                                  >
                                    <Trash2 size={13} />
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}

                        {selectedLoteItems.length === 0 && (
                          <tr>
                            <td colSpan="7" className="py-10 text-center text-slate-400">
                              <div className="space-y-1">
                                <p className="font-bold text-slate-600">Este lote ainda não possui itens.</p>
                                <p className="text-xs text-slate-400">Use a barra acima para adicionar SKUs e quantidades manualmente ou aloque itens via Análise de SKUs.</p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                </div>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-slate-400 text-xs">
                  <ClipboardList size={32} className="mb-2 text-slate-300" />
                  <span>Selecione um lote da lista para gerenciar itens, prazos e lançar na fábrica.</span>
                </div>
              )}
            </div>

          </div>
        )}

        {/* ========================================== */}
        {/* SUBTAB 4: SAZONALIDADE */}
        {/* ========================================== */}
        {activeSubTab === "sazonalidade" && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 animate-fade-in">
            
            {/* Category x Month Grid */}
            <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm lg:col-span-3 overflow-x-auto flex flex-col">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-xs font-black text-slate-700 uppercase tracking-wider">Fatores de Sazonalidade</h3>
                  <p className="text-[10px] text-slate-400 mt-1">Configure os fatores multiplicadores para cada Categoria x Mês.</p>
                </div>
                <div className="flex gap-2">
                  {Object.keys(pendingSeasonality).length > 0 && (
                    <button
                      onClick={handleSaveSeasonalityChanges}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-750 text-white rounded-lg text-[10px] font-black shadow transition-all uppercase tracking-wider flex items-center gap-1 animate-pulse"
                    >
                      <Check size={12} /> Salvar Alterações
                    </button>
                  )}
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-center text-xs border border-collapse border-slate-200">
                  <thead className="bg-slate-50 font-black uppercase text-slate-500">
                    <tr>
                      <th className="p-3 text-left border border-slate-200">Categoria</th>
                      {MONTH_NAMES.slice(1).map((m, idx) => (
                        <th key={m} className="p-2 border border-slate-200 w-16 text-[10px]">{m.substring(0, 3)}</th>
                      ))}
                      <th className="p-2 border border-slate-200 w-20 text-[10px]">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {PLANNING_CATEGORIES.map(cat => {
                      return (
                        <tr key={cat} className="hover:bg-slate-50">
                          <td className="p-3 text-left font-bold text-slate-700 border border-slate-200 bg-slate-50/50">
                            {cat}
                          </td>
                          {Array.from({ length: 12 }, (_, mIdx) => {
                            const month = mIdx + 1;
                            const record = seasonalityData.find(s => s.categoria === cat && s.mes === month);
                            const recordId = record ? record.id : null;
                            const pendingVal = recordId ? pendingSeasonality[recordId] : undefined;
                            
                            const displayFactor = pendingVal !== undefined ? pendingVal : (record ? record.fator : 1.0);
                            const numericFactor = parseFloat(displayFactor) || 1.0;

                            // Color scale mapping based on factor
                            let colorClass = "bg-white text-slate-700";
                            if (numericFactor > 1.1) colorClass = "bg-emerald-800 text-white font-bold";
                            else if (numericFactor > 1.0) colorClass = "bg-emerald-100 text-emerald-800 font-bold";
                            else if (numericFactor < 0.9) colorClass = "bg-orange-100 text-orange-850 font-bold";
                            else if (numericFactor < 1.0) colorClass = "bg-yellow-50 text-yellow-800 font-bold";

                            return (
                              <td key={month} className={`p-1 border border-slate-200 ${colorClass} w-16`}>
                                <input
                                  type="number"
                                  step="0.1"
                                  min="0.1"
                                  max="2.0"
                                  className="w-full bg-transparent text-center border-0 focus:outline-none font-mono focus:ring-1 focus:ring-blue-500 rounded p-1 text-xs"
                                  value={displayFactor}
                                  onChange={(e) => {
                                    if (recordId) {
                                      handleSeasonalityLocalChange(recordId, e.target.value);
                                    }
                                  }}
                                />
                              </td>
                            );
                          })}
                          <td className="p-2 border border-slate-200">
                            <button
                              onClick={() => handleResetRow(cat)}
                              className="text-slate-400 hover:text-red-500 font-bold text-[9px] tracking-wider uppercase border border-slate-200 hover:border-red-300 rounded px-1.5 py-0.5 transition-colors"
                            >
                              Reset
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Sazonalidade Audit Sidebar */}
            <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex flex-col h-[500px]">
              <h3 className="text-xs font-bold text-slate-700 mb-4 uppercase tracking-wider border-b pb-2">Histórico de Alterações</h3>
              <div className="flex-1 overflow-y-auto space-y-4 pr-1 text-xs">
                {auditLogs.length > 0 ? (
                  auditLogs.map(log => (
                    <div key={log.id} className="p-3 bg-slate-50 border rounded-lg space-y-1">
                      <div className="flex justify-between font-bold text-slate-600 text-[10px]">
                        <span>{log.categoria}</span>
                        <span className="font-mono text-[9px] bg-slate-200 px-1 rounded">{MONTH_NAMES[log.mes].substring(0, 3)}</span>
                      </div>
                      <p className="text-[10px] text-slate-400">
                        Modificado por <strong className="text-slate-650">{log.updated_by_name}</strong>
                      </p>
                      <div className="flex justify-between items-center text-[9px] pt-1">
                        <span className="text-slate-400">{new Date(log.updated_at).toLocaleDateString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</span>
                        <span className="font-mono font-bold text-slate-700">
                          {log.old_value} → {log.new_value}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-400 text-center">
                    Nenhuma alteração registrada ainda.
                  </div>
                )}
              </div>
            </div>

          </div>
        )}

        {/* ========================================== */}
        {/* SUBTAB 5: CONFIGURAÇÕES */}
        {/* ========================================== */}
        {activeSubTab === "configuracoes" && (
          <div className="max-w-xl mx-auto bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-6 animate-fade-in">
            <div className="flex items-center gap-2 border-b pb-3">
              <Settings className="text-blue-600" size={16} />
              <h3 className="text-xs font-black text-slate-700 uppercase tracking-wider">Configurações Globais de Fabricação</h3>
            </div>

            <div className="space-y-4">
              
              <div className="space-y-1">
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider">Prazo de Fabricação Padrão (Dias):</label>
                <input
                  type="number"
                  min="1"
                  className="w-full p-2.5 border rounded-lg text-xs outline-none bg-slate-50 focus:ring-2 focus:ring-blue-500 font-bold text-slate-700"
                  value={configs.prazo_fabricacao_dias}
                  onChange={(e) => setConfigs(prev => ({ ...prev, prazo_fabricacao_dias: Number(e.target.value) }))}
                />
                <span className="text-[10px] text-slate-400 block">Número de dias acrescidos à data de início para obter a previsão de entrega do lote.</span>
              </div>

              <div className="space-y-1">
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider">Buffer de Segurança (%):</label>
                <input
                  type="number"
                  min="0"
                  className="w-full p-2.5 border rounded-lg text-xs outline-none bg-slate-50 focus:ring-2 focus:ring-blue-500 font-bold text-slate-700"
                  value={configs.buffer_seguranca_percentual}
                  onChange={(e) => setConfigs(prev => ({ ...prev, buffer_seguranca_percentual: Number(e.target.value) }))}
                />
                <span className="text-[10px] text-slate-400 block">Percentual de margem de segurança adicionado ao estoque mínimo sugerido.</span>
              </div>

              <div className="space-y-1">
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider">Janela de Histórico de Vendas (Meses):</label>
                <input
                  type="number"
                  min="1"
                  className="w-full p-2.5 border rounded-lg text-xs outline-none bg-slate-50 focus:ring-2 focus:ring-blue-500 font-bold text-slate-700"
                  value={configs.janela_historico_meses}
                  onChange={(e) => setConfigs(prev => ({ ...prev, janela_historico_meses: Number(e.target.value) }))}
                />
                <span className="text-[10px] text-slate-400 block">Período de vendas considerado para calcular a média de vendas mensal.</span>
              </div>

              <div className="space-y-1">
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider">Capacidade Semanal da Oficina (Peças):</label>
                <input
                  type="number"
                  min="0"
                  placeholder="Deixe 0 ou vazio para usar a capacidade calculada (7 dias)"
                  className="w-full p-2.5 border rounded-lg text-xs outline-none bg-slate-50 focus:ring-2 focus:ring-blue-500 font-bold text-slate-700"
                  value={configs.oficina_capacidade_semanal || ""}
                  onChange={(e) => {
                    const newVal = e.target.value === "" ? 0 : Number(e.target.value);
                    setConfigs(prev => ({ ...prev, oficina_capacidade_semanal: newVal }));
                  }}
                />
                <span className="text-[10px] text-slate-400 block">Capacidade máxima semanal de produção da oficina. Defina um valor maior que 0 para sobrescrever (override) ou deixe 0/vazio para usar a capacidade calculada com base no número de peças finalizadas na fábrica nos últimos 7 dias.</span>
              </div>

            </div>

            <div className="flex justify-end border-t pt-4">
              <button
                onClick={handleSaveConfigs}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-black shadow flex items-center gap-1.5 uppercase tracking-wider transition-colors"
              >
                <Check size={14} /> Salvar Configurações
              </button>
            </div>
          </div>
        )}

      </div>

      {/* ========================================== */}
      {/* DIALOG 1: DEFINE STOCK MINIMUM MODAL */}
      {/* ========================================== */}
      {editingItemForMin && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-2xl border border-slate-200 flex flex-col space-y-4 animate-scale-in">
            
            <div className="flex justify-between items-start border-b pb-3">
              <div>
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider">Configurar Estoque Mínimo</span>
                <strong className="text-sm font-black text-slate-800 block uppercase font-mono">{editingItemForMin.sku}</strong>
              </div>
              <button
                onClick={() => setEditingItemForMin(null)}
                className="p-1 text-slate-450 hover:text-slate-700 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Sales month-by-month history */}
            <div className="space-y-3">
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">Histórico Mensal de Vendas</span>
              
              <div className="grid grid-cols-6 gap-2">
                {editingItemForMin.historico_mensal && editingItemForMin.historico_mensal.map(h => {
                  const parts = h.mes.split("-");
                  const monthName = MONTH_NAMES[Number(parts[1])].substring(0, 3);
                  return (
                    <div key={h.mes} className="bg-slate-50 border p-2 rounded-lg text-center shadow-inner">
                      <span className="text-[8px] font-bold text-slate-400 block uppercase">{monthName}</span>
                      <strong className="text-xs text-slate-800 font-mono">{h.vendas}</strong>
                    </div>
                  );
                })}
              </div>

              {/* General metrics */}
              <div className="grid grid-cols-3 gap-4 bg-slate-50 p-3 rounded-lg border text-xs">
                <div>
                  <span className="text-[9px] text-slate-400 block">Estoque Atual:</span>
                  <strong className="text-slate-800 font-bold">{editingItemForMin.estoque_atual} pçs</strong>
                </div>
                <div>
                  <span className="text-[9px] text-slate-400 block">Previsão 30d:</span>
                  <strong className="text-slate-800 font-bold">{editingItemForMin.previsao_proximos_30_dias} pçs</strong>
                </div>
                <div>
                  <span className="text-[9px] text-slate-400 block">Sugerido:</span>
                  <strong className="text-blue-600 font-black">{editingItemForMin.estoque_minimo_sugerido} pçs</strong>
                </div>
              </div>

              {/* Define stock input */}
              <div className="space-y-1.5 pt-2">
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">Definir Estoque Mínimo (Mão do Gestor):</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    min="0"
                    className="flex-1 p-2 border rounded-lg text-xs outline-none bg-slate-50 focus:ring-2 focus:ring-blue-500 font-bold"
                    placeholder="Deixe em branco para remover mínimo definido"
                    value={newMinVal}
                    onChange={(e) => setNewMinVal(e.target.value)}
                  />
                  {editingItemForMin.estoque_minimo_definido !== editingItemForMin.estoque_minimo_sugerido && (
                    <button
                      onClick={() => setNewMinVal(String(editingItemForMin.estoque_minimo_sugerido))}
                      className="px-3 bg-slate-100 hover:bg-slate-200 border rounded-lg text-[10px] font-bold text-slate-600 uppercase"
                    >
                      Usar Sugerido ({editingItemForMin.estoque_minimo_sugerido})
                    </button>
                  )}
                </div>
                <span className="text-[9px] text-slate-400 block">Se definido, o planejamento considerará este valor de estoque mínimo em vez do sugerido por inteligência de vendas.</span>
              </div>

            </div>

            <div className="flex gap-3 justify-end border-t pt-4">
              <button
                onClick={() => setEditingItemForMin(null)}
                className="px-4 py-2 border rounded-lg text-xs font-bold text-slate-500 hover:bg-slate-50 uppercase"
              >
                Voltar
              </button>
              <button
                onClick={handleSaveDefinedMin}
                className="px-5 py-2 bg-blue-600 hover:bg-blue-750 text-white rounded-lg text-xs font-black shadow uppercase tracking-wider"
              >
                Salvar Mínimo
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ========================================== */}
      {/* DIALOG 2: CREATE BATCH (LOTE) MODAL */}
      {/* ========================================== */}
      {showCreateLoteModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl border border-slate-200 flex flex-col space-y-4 animate-scale-in">
            
            <div className="flex justify-between items-start border-b pb-3">
              <div>
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider">Gestão de Lotes</span>
                <strong className="text-sm font-black text-slate-800 block uppercase">Criar Novo Lote de Fabricação</strong>
              </div>
              <button
                onClick={() => setShowCreateLoteModal(false)}
                className="p-1 text-slate-450 hover:text-slate-700 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-4">
              
              <div className="space-y-1">
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">Nome do Lote:</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded-lg text-xs outline-none bg-slate-50 focus:ring-2 focus:ring-blue-500 font-semibold text-slate-850"
                  placeholder="Ex: Lote 1 - Urgentes"
                  value={newLoteForm.nome}
                  onChange={(e) => setNewLoteForm(prev => ({ ...prev, nome: e.target.value }))}
                />
              </div>

              <div className="space-y-1">
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">Data de Início Prevista:</label>
                <input
                  type="date"
                  className="w-full p-2 border rounded-lg text-xs outline-none bg-slate-50 focus:ring-2 focus:ring-blue-500 font-semibold text-slate-850"
                  value={newLoteForm.data_inicio_prevista}
                  onChange={(e) => setNewLoteForm(prev => ({ ...prev, data_inicio_prevista: e.target.value }))}
                />
              </div>

              <div className="space-y-1">
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">Observações / Anotações:</label>
                <textarea
                  className="w-full p-2 border rounded-lg text-xs outline-none bg-slate-50 focus:ring-2 focus:ring-blue-500 h-20 resize-none text-slate-700"
                  placeholder="Descreva detalhes específicos para a oficina..."
                  value={newLoteForm.observacoes}
                  onChange={(e) => setNewLoteForm(prev => ({ ...prev, observacoes: e.target.value }))}
                />
              </div>

            </div>

            <div className="flex gap-3 justify-end border-t pt-4">
              <button
                onClick={() => setShowCreateLoteModal(false)}
                className="px-4 py-2 border rounded-lg text-xs font-bold text-slate-500 hover:bg-slate-50 uppercase"
              >
                Voltar
              </button>
              <button
                onClick={handleCreateLote}
                className="px-5 py-2 bg-blue-600 hover:bg-blue-750 text-white rounded-lg text-xs font-black shadow uppercase tracking-wider"
              >
                Criar Lote
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}
